import { useEffect, useState } from 'react';
import {
  api, baustelle, darf, datum, menge, uhr, auswahllisten, GEWERK_TEXT,
  ZUSTAND_TEXT, zustandText,
  type Anmelder, type Auswahllisten, heuteIso, pushFrage} from '../api.ts';
import {
  Auswahl, Dialog, Fehlerkasten, Formular, Haken, HakenReihe, Text, ausFehler,
  useEntwurf, type Fehlerlage,
} from '../teile/Formular.tsx';
import { Klappmenue } from '../teile/Geruest.tsx';
import AkteUebersicht from './AkteUebersicht.tsx';
import Aufmass, { MitGrund } from './Aufmass.tsx';
import TerminStornoFenster from '../teile/TerminStornoFenster.tsx';
import Dateien from './Dateien.tsx';
import Behoerdenmappe from './Behoerdenmappe.tsx';
import Leb from './Leb.tsx';
import Vorbereiten from './Vorbereiten.tsx';
import Rotberichtigung from './Rotberichtigung.tsx';
import {
  EinblasstopDialog, NachrichtDialog, PollerHinweis,
} from '../teile/Betrieb.tsx';
import { MailDialog } from '../teile/AkteMail.tsx';
import { auftragsBetreff, quicklink } from '../teile/adresse.ts';
import { MailMitAdressbuch } from '../teile/MailMitAdressbuch.tsx';
import { FertigDialog } from '../teile/Fertigmeldung.tsx';
import Nachfragen from '../teile/Nachfragen.tsx';
import Terminkalender, { type Bautag } from '../teile/Terminkalender.tsx';
import { BauakteDialog } from '../teile/Bauakte.tsx';
import { DokuDialog, WfmtDialog } from '../teile/Doku.tsx';
import {
  BaustelleFertigDialog, NichtErledigtDialog, TechnikmeldungDialog,
} from '../teile/Rueckmeldung.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { PartnerfreigabeDialog } from '../teile/Partnerfreigabe.tsx';

// Die Akte eines Auftrags.
//
// Marvin, 23.08.2026: „vor allem fehlen mir die ganzen möglichkeiten alles zu
// enditieren, anpassungen zu machen, fehler zu ändern."
//
// Deshalb ist hier alles, was zu einem Auftrag gehoert, an einer Stelle UND
// aenderbar: die Stammangaben, die Termine, das Aufmass, die Dateien, die
// Behoerdenpapiere. Jeder Reiter ist ein eigener Bildschirm, damit keiner
// davon zu einer Wand aus Feldern wird.

/* DIE REITER EINER AKTE.
 *
 * ORIGINALTEXT MARVIN (26.08.2026)
 *   „Auch das Aufmaß muss im Auftrag bei sein. Kein extra Punkt."
 *   „Die brauchen einen Workflow so wie jetzt wo die sich durchklicken mit
 *    allem."
 *
 * ES WAREN ZWEI REITER AUF DIESELBEN DATEIEN, UND JETZT IST ES EINER.
 *
 * ORIGINALTEXT MARVIN (26.08.2026)
 *   „auch brauche ich ja nicht Dateien und Dokumentation. einmal reicht mir
 *    die ordnerstruktur zum auftrag mit den dateien."
 *
 * „Dokumentation" war die Ordneransicht des Monteurs, „Dateien" die
 * Tabellenansicht des Innendienstes - dieselben `datei_bezug`-Zeilen
 * derselben Ordner desselben Auftrags, zweimal. Wer eine Datei suchte, musste
 * raten, in welchem der beiden sie steht. „Dateien" kann jetzt beides und
 * zeigt jedem das Seine.
 *
 * DAS RECHT `akte.dokumentation` BLEIBT und wird nicht abgeschafft, weil ein
 * Bildschirm verschwindet: es entscheidet jetzt, ob die Zeile „so weit sind
 * die Gewerke" im Dateireiter steht. Ein Recht zu löschen nimmt es allen, die
 * es haben, und niemand merkt, was ihm fehlt.
 */
const REITER = [
  { schluessel: 'uebersicht',    name: 'Übersicht',     recht: 'akte.lesen' },
  { schluessel: 'aufmass',       name: 'Aufmaß',        recht: 'auftrag.lesen' },
  /* `datei.lesen`, NICHT `datei.suchen`. Hier stand die Suche über ALLE
     Aufträge, und das ist eine andere Frage als „was liegt an DIESEM". Wer
     die Suche nicht braucht (Marvin, 26.08.2026: „,dateien' brauche ich da
     auch nicht"), hätte damit auch die Dateien seines eigenen Auftrags
     verloren - also genau den Bildschirm, auf dem die Ordnerstruktur steht. */
  { schluessel: 'dateien',       name: 'Dateien',       recht: 'datei.lesen' },
  /* ORIGINALTEXT MARVIN (02.09.2026): „das heißt „Dokumente erzeugen" auf dem
     registerblatt ist es auch falsch". Der Reiter hiess „Behörde", das Fenster
     darin „Behördenpapier erzeugen" und der Knopf „Papier erzeugen" - drei
     Namen für eine Sache, und keiner davon der, unter dem man sie sucht. */
  { schluessel: 'behoerde',      name: 'Dokumente erzeugen',
    recht: 'dokument.erzeugen' },
  /* DIE ROTBERICHTIGUNG IST EIN REITER UND NICHT NUR EIN FENSTER.
     ========================================================================
     ORIGINALTEXT MARVIN (18.09.2026)
       „ich kann die Rotberichtigung bei der erstellung des LEB nicht öffnen.
        oben fehlt der reiter."

     Sie stand ausschliesslich im Klappmenü „Vorgang" - als Fenster, das den
     Bildschirm zudeckt, mit dem man sie vergleichen will. Sie steht VOR dem
     LEB, weil sie davor kommt: erst die Nachweise, dann die Abrechnung.
     Das Fenster über „Vorgang" bleibt daneben bestehen; wer es kennt,
     verliert keinen Weg. */
  { schluessel: 'rb',            name: 'Rotberichtigung',
    recht: 'aufmass.rotberichtigung' },
  { schluessel: 'leb',           name: 'LEB',           recht: 'abrechnung.leb' },
];

// Die Auswahllisten kommen von /api/auswahl. Hier stand bis zum 23.08.2026
// eine eigene Fassung von BEREICHE; im Schreibweg stand eine zweite und in
// den Regeln eine dritte. Drei Listen fuer dieselbe Spalte.
//
// Solange die Antwort noch nicht da ist, zeigt ein Auswahlfeld eine leere
// Liste. Das ist richtig so: lieber kurz nichts zur Auswahl als eine
// Notliste, die etwas anderes behauptet als der Server.
const KEINE: any[] = [];

/**
 * Was aus dem gewaehlten Auftragstyp folgt, als Satz unter dem Feld.
 *
 * ORIGINALTEXT MARVIN (25.08.2026)
 *   „dieser text ,unsere Gewerke: Tiefbau, Einblasen, Spleißen' und wenn es da
 *    noch andere texte gibt bei den anderen auftragstypen kommen weg."
 *
 * DIE AUFZAEHLUNG DER EIGENEN GEWERKE IST WEG, und zwar hier genauso wie in
 * der Uebersicht. Sie sagte dem Innendienst, was er weiss, und stand neben
 * denselben Gewerken als Kachel.
 *
 * WAS BLEIBT, IST DIE FOLGE, DIE MAN NICHT SIEHT: welches Gewerk bei diesem
 * Typ NICHT unseres ist und deshalb in keiner Liste offener Arbeiten
 * auftaucht. Das ist keine Wiederholung, sondern der Grund, warum ein Auftrag
 * ohne Spleisstermin trotzdem fertig sein kann. Bei einem Typ, der alle drei
 * Gewerke umfasst, steht hier nichts mehr.
 */
function typHinweis(listen: Auswahllisten | null, typ: string): string {
  if (!typ) return '';
  const g = listen?.auftragstyp.find((x) => x.wert === typ)?.gewerke;
  if (!g) return '';
  const fremd = [
    !g.tiefbau && 'Tiefbau', !g.einblasen && 'Einblasen',
    !g.spleissen && 'Spleißen',
  ].filter(Boolean).join(', ');
  return fremd ? `${fremd} zählt bei diesem Typ nicht als offen.` : '';
}

export default function Auftragsakte({
  ich, id, zurueck, zurueckName, starteAuf, reiterGewechselt,
}: {
  ich: Anmelder; id: string; zurueck: () => void;
  /** Wie der Bildschirm heisst, von dem aus die Akte geöffnet wurde. */
  zurueckName?: string;
  /** Auf welchem Reiter die Akte aufgeht. Siehe `oeffne` in App.tsx. */
  starteAuf?: string;
  /** Der Reiter hat gewechselt: für die Adresse, damit er das Neuladen übersteht (.217). */
  reiterGewechselt?: (reiter: string) => void;
}) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [wo, setWo] = useState(starteAuf ?? 'uebersicht');
  useEffect(() => { reiterGewechselt?.(wo); }, [wo]);
  const [dialog, setDialog] = useState<
    null | 'bearbeiten' | 'kommentar' | 'termin' | 'archivieren'
    | 'vorbereiten' | 'rb' | 'einblasstop' | 'nachricht' | 'mail' | 'fertig' | 'bauakte' | 'partner'
    | 'doku' | 'wfmt' | 'nachfassen'
    /* WAS VON DER BAUSTELLE ZURÜCKKOMMT, seit dem 08.09.2026. Beide sind
       Fenster und keine Reiter: sie werden von jemandem bedient, der vor dem
       Loch steht, und sie beantworten je eine einzelne Frage. */
    | 'technikmeldung' | 'nicht_erledigt'
    /* UND DIE KOLONNE IST ABGEFAHREN, DIE UNTERLAGEN KOMMEN NACH (15.09.2026).
       Dritter Fall derselben Sorte: ein Fenster für eine einzelne Frage, von
       jemandem bedient, der gerade einpackt. */
    | 'baustelle_fertig'>(null);
  /* AUF WELCHEM REITER DAS BEARBEITEN-FENSTER AUFGEHT. Er steht hier und
     nicht im Fenster selbst: der Chip in der Übersicht bestimmt ihn, und das
     Fenster entsteht erst danach. */
  const [bearbeitenBereich, setBearbeitenBereich] =
    useState<'anschrift' | 'kennungen' | 'auftrag' | 'netz' | 'kunde' | 'stand'>(
      'anschrift');
  /* AUF WELCHER SEITE DAS VORBEREITEN-FENSTER AUFGEHT. Dieselbe Bauart und
     derselbe Grund wie beim Bereich darüber: der Chip in der Karte
     „Tiefbauaufwand" heisst „Planung öffnen" und landete bis .137 auf Seite 1,
     also beim Lageplan für die Gemeinde. Der Knopf „Vorbereiten" oben in der
     Werkzeugleiste bleibt bei Seite 1 - er verspricht nichts anderes. */
  const [vorbereitenSeite, setVorbereitenSeite] = useState(1);

  const darfSchreiben = darf(ich, 'auftrag.schreiben');
  const darfTermin = darf(ich, 'termin.vergeben');
  const darfVorbereiten = darf(ich, 'vorbereiten.pflegen');
  const darfRb = darf(ich, 'aufmass.rotberichtigung');
  const darfAufmass = darf(ich, 'aufmass.erfassen');
  const darfSenden = darf(ich, 'nachricht.senden');
  const [linkKopiert, setLinkKopiert] = useState<string | null>(null);
  const [mailOffen, setMailOffen] = useState(false);
  const darfPruefen = darf(ich, 'aufmass.pruefen');
  // Der Rückkanal hat seit Migration 040 ein EIGENES Recht. Vorher hing er an
  // `aufmass.erfassen`: der Monteur durfte zurückmelden, weil er auch Mengen
  // einträgt. Das ist ein Nebeneffekt und kein Schalter.
  const darfMelden = darf(ich, 'akte.rueckmeldung');
  // Die Leiste erscheint, sobald EIN Knopf darin trägt. Ein Sub hat
  // `aufmass.erfassen` und bekommt damit Einblasstop, Fertig melden und die
  // Rückmeldung; alles andere bleibt ihm verborgen.
  const darfWerkzeuge = darfSchreiben || darfAufmass || darfSenden || darfMelden
                        || darfPruefen;

  async function laden() {
    try { setD(await api.auftrag(id)); setFehler(null); }
    catch (f) { setFehler(ausFehler(f)); }
  }
  // Ein Wechsel des Auftrags setzt auf den gewuenschten Reiter zurueck, nicht
  // stur auf die Uebersicht: wer aus der Behoerdenmappe kommt, will dorthin.
  useFrisch(laden);
  useEffect(() => { setD(null); setWo(starteAuf ?? 'uebersicht'); laden(); },
            [id, starteAuf]);

  if (fehler && !d) {
    return (
      <div className="inhalt">
        <Fehlerkasten lage={fehler} />
        <button className="knopf" style={{ marginTop: 12 }} onClick={zurueck}>
          Zurück zur Liste
        </button>
      </div>
    );
  }
  if (!d) return null;

  const k = d.kopf;
  const reiter = REITER.filter((r) => darf(ich, r.recht));

  /* EIN UNBEKANNTER REITER FÄLLT AUF DEN 1. ERLAUBTEN ZURÜCK.
   *
   * ORIGINALTEXT MARVIN (22.09.2026)
   *   „hier scheint ein fehler in der darstellung sein."
   *
   * Dazu sein Bildschirmfoto: die Akte offen, die Reiterleiste da, und
   * darunter eine LEERE FLÄCHE. Der Aufruf kam aus der LEB-Abrechnung und
   * nannte den Reiter „abrechnung" - den es hier nie gab. Kein Zweig traf
   * zu, und übrig blieb nichts.
   *
   * **EIN BILDSCHIRM, DER BEI EINEM TIPPFEHLER IM AUFRUF LEER BLEIBT,
   * MACHT AUS EINEM FALSCHEN WORT EINEN KAPUTTEN BILDSCHIRM** - und wer
   * ihn sieht, sucht den Fehler an der Akte statt am Aufruf. Der Aufruf
   * ist berichtigt; dieser Riegel steht trotzdem, denn der nächste falsche
   * Schlüssel kommt bestimmt.
   *
   * ZURÜCK AUF DEN 1. ERLAUBTEN und nicht fest auf „uebersicht": wer
   * `akte.lesen` nicht hat, hat auch die Übersicht nicht, und dann wäre
   * der Rückfall genauso leer wie der Fehler. */
  const gezeigt = reiter.some((r) => r.schluessel === wo)
    ? wo : (reiter[0]?.schluessel ?? wo);

  return (
    <>
      <div className="kopf">
        <button className="knopf" onClick={zurueck}>
          &larr; {zurueckName ?? 'Liste'}
        </button>
        {/* DIE GANZE ANSCHRIFT, nicht nur Strasse und Nummer.
            Marvin, 25.08.2026: „bitte oben im titel die gesamte adresse
            reinschreiben."
            Eine „Hauptstrasse 12" gibt es in jedem zweiten Ort des Gebiets.
            Wer zwei Akten nebeneinander offen hat, unterschied sie bis heute
            nur an der Kennung weiter unten. */}
        <h1>
          {k.strasse} {k.hausnummer}{k.hausnummer_zusatz ?? ''}
          {(k.plz || k.ort_text) && (
            <span className="ortzeile">
              , {[k.plz, k.ort_text].filter(Boolean).join(' ')}
            </span>
          )}
        </h1>
        {/* DIESELBE MARKE WIE IN DER LISTE, aus derselben Funktion.
            Marvin, 22.09.2026: „Warum stehen hier Aufträge auf
            «überfällig» wenn der Termin erst in in der Zukunft liegt?" Sie
            sagt es jetzt, und sie sagt es an beiden Stellen gleich. */}
        <span className={'marke-zustand z-' + k.zustand}
              title={k.zustand === 'ueberfaellig'
                       && k.ueberfaellig_nachfolger_geplant
                     ? 'Für ein späteres Gewerk steht schon ein Termin. '
                       + 'Dann ist die Arbeit meist getan und nur der '
                       + 'Haken fehlt.'
                     : undefined}>
          {zustandText(k)}
        </span>
        {/* DER ZWEITE CHIP, WIE IN DER LISTE (.329). ORIGINALTEXT MARVIN
            (07.10.2026): „ich möchte, dass oben im einzelnen auftrag auch der
            zweite chip angezeigt wird, wie "Abgerechnet", "Wartet" usw."
            Dieselbe Sicht wie die Liste (`v_auftrag_ansteht`), nichts hier
            neu gerechnet. */}
        {k.ansteht?.abgerechnet && (
          <span className="marke-zustand z-abgerechnet"
                title="Es liegt ein LEB vor, und es steht keine Position mehr offen.">Abgerechnet</span>
        )}
        {k.ansteht?.abrechenbar && (
          <span className="merker" title={'Das Aufmaß ist abgegeben, mindestens eine Position ist noch in kein LEB gegangen: Reiter „LEB".'}>Abrechenbar</span>
        )}
        {k.ansteht?.aufmass_pruefen && (
          <span className="merker" title="Der Partner hat sein Aufmaß eingereicht und wartet auf das Gegenhalten.">Aufmaß prüfen</span>
        )}
        {k.ansteht?.doku_pruefen && (
          <span className="merker" title="Die Montage-Dokumentation ist eingereicht und noch nicht abgeschlossen.">Doku prüfen</span>
        )}
        {k.wartet && (
          <span className="merker warnung" title={k.wartegrund ?? undefined}>
            Wartet
          </span>
        )}
        {k.ansteht?.wartegrund_fehlt && (
          <span className="merker warnung" title={'Der Auftrag steht, aber es ist kein Wartegrund eingetragen: „Bearbeiten → Stand".'}>Wartegrund fehlt</span>
        )}
        {k.ansteht?.einblasstopp && (
          <span className="merker warnung" title="Beim Einblasen ist ein Stop gemeldet und noch nicht zurückgenommen.">Einblasstopp</span>
        )}
        {k.archiviert_am && <span className="merker">archiviert</span>}
        {/* DER QUICKLINK (.332). Marvin, 07.10.2026: „den kann man dann auch
            kopieren und somit an die firma in telegram senden, einfach etwas
            schneller." Kopiert wird eine Zeile wie bei der Telekom:
            „MDS Link: https://…/#/a/<KLS-ID>". */}
        <button type="button" className="knopf klein" title={quicklink(k)}
                onClick={async () => {
                  const text = `MDS Link: ${quicklink(k)}`;
                  try { await navigator.clipboard.writeText(text); setLinkKopiert('kopiert'); }
                  catch { setLinkKopiert(text); }
                  setTimeout(() => setLinkKopiert(null), 4000);
                }}>
          Link kopieren
        </button>
        {/* .593: DEN AUFTRAG PER MAIL WEITERGEBEN, Anschrift, KLS-ID und SM im Betreff. */}
        <button type="button" className="knopf klein" title="Empfänger aus dem Adressbuch wählen, dann öffnet sich das Mailprogramm mit dem Auftrag im Betreff" onClick={() => setMailOffen(true)}>E-Mail</button>
        {mailOffen && <MailMitAdressbuch bereich="ne3" betreff={auftragsBetreff({ kls_id: k.kls_id, strasse: k.strasse, hausnummer: k.hausnummer, hausnummer_zusatz: k.hausnummer_zusatz, plz: k.plz, ort: k.ort_text, sm: k.sm_nummer })} zu={() => setMailOffen(false)} />}
        {linkKopiert && <span className="klein leise" style={{ wordBreak: 'break-all' }}>{linkKopiert}</span>}
        {/* .586: Route aus der Akte (Start, Stopp), wie in der Karte. */}
        {/* WARUM LIEGEN HIER ZWEI AUFTRÄGE AUF EINER ANSCHRIFT? Genau das
            beantwortet diese Marke. Ohne sie sieht der zweite aus wie ein
            Doppelgänger, und wer aufräumt, führt zwei Vorgänge zusammen, die
            zwei sind. */}
        {k.maengelbeseitigung && (
          <span className="merker warnung"
                title="Eigene BA/FA-ID und eigene SM-Nummer; kein Doppelgänger.">
            Mängelbeseitigung
          </span>
        )}
        <div className="wachsen" />
        {/* DIE KENNUNGEN STEHEN NICHT MEHR HIER.
            Marvin, 25.08.2026: „entferne oben im kopf die SM und KLS ID."
            Sie stehen jetzt sauber getrennt und ganz oben in der Karte
            „Baustelle", also dort, wo man sie abliest und weitergibt. Im Kopf
            haben sie nur Platz belegt, den die Werkzeuge brauchen. */}
        {/* DIE LEISTE HING AN EINEM EINZIGEN RECHT, UND DAS WAR DER FEHLER.
            ORIGINALTEXT MARVIN (26.08.2026)
              „Wie ist die auftragsverwaltung, Seite/funktionen ... für die
               subs, ist das alles für die gebaut?"
            Hier stand `darfSchreiben && ...`, also `auftrag.schreiben`. Darin
            steckten aber auch die beiden Knöpfe, die der SERVER dem Sub
            ausdrücklich erlaubt: „Fertig melden" und „Einblasstop" verlangen
            `aufmass.erfassen`, und `fertig.ts` beschreibt den Sub-Fall in
            eigenen Absätzen. Der Server hat auf einen Knopf gewartet, den es
            nicht gab. Jetzt entscheidet JEDER Knopf sein eigenes Recht, und
            die Leiste erscheint, sobald einer davon trägt. */}
        {darfWerkzeuge && !k.archiviert_am && (
          <div className="reihe werkzeuge">
            {/* WAS AM VORGANG GESCHIEHT, in der Reihenfolge, in der es
                geschieht: vorbereiten, terminieren, unterbrechen, fertig
                melden, wieder aufmachen, abgeben. */}
            <Klappmenue titel="Vorgang" kinder={[
              darfVorbereiten && (
                <button key="v" onClick={() => {
                  /* DER KNOPF OBEN VERSPRICHT DIE GANZE VORBEREITUNG, ALSO
                     FÄNGT ER VORNE AN. Ohne diese Zeile bliebe die Seite
                     stehen, die zuletzt ein Chip gesetzt hat - und dann führt
                     derselbe Knopf beim zweiten Mal woandershin als beim
                     ersten. */
                  setVorbereitenSeite(1); setDialog('vorbereiten');
                }}>
                  Vorbereiten
                  <small>Lageplan, Angaben für AAZ und VAO, Tiefbauschätzung</small>
                </button>
              ),
              darfTermin && (
                <button key="t" onClick={() => setDialog('termin')}>
                  Termin
                  <small>Firma und Tag je Gewerk</small>
                </button>
              ),
              /* DER EINBLASSTOP GEHOERT AN DEN AUFTRAG, nicht in eine
                 Sammelliste. Er wird festgestellt, waehrend jemand vor dem
                 Rohr steht, und die Frage lautet dann „an welchem Auftrag
                 bin ich gerade". */
              /* UND DER INNENDIENST MELDET IHN AUCH.
                 ORIGINALTEXT MARVIN (03.09.2026): „ist der bereich schon im
                 innendienst vorhanden das wir «Einblasstop melden»? bitte mal
                 prüfen" - nachgesehen: nein. Der Knopf hing an
                 `aufmass.erfassen`, und das trägt der Innendienst nicht. Wer
                 anruft und die Meter durchgibt, hatte niemanden, der sie
                 einträgt. Der Weg im Server ist im selben Zug geweitet
                 worden; ein Knopf ohne Weg wäre ein 403. */
              ((darfAufmass || darfPruefen) || (darfSenden && k?.einblasstop_meter != null)) && (
                <button key="e" onClick={() => setDialog('einblasstop')}>
                  Einblasstop
                  <small>{k?.einblasstop_meter != null
                    ? 'Gemeldet: ansehen und an die Telekom senden'
                    : 'Meter und Richtung, je Seite eine Meldung'}</small>
                </button>
              ),
              /* DER SCHRITT VOR DEM FERTIGMELDEN, UND DESHALB STEHT ER
                 DAVOR.
                 ORIGINALTEXT MARVIN (15.09.2026): „die partner wünschen sich
                 noch eine funktion, wo sie den auftrag per knopf fertig
                 melden können, was wir im innendienst sehen, aber sie das
                 aufmaß und die unterlagen später hochladen."
                 Er hängt an denselben 2 Rechten wie „Fertig melden" und
                 braucht KEIN neues: wer fertig melden darf, darf auch sagen,
                 dass draussen nichts mehr zu tun ist. Der Pflichtordnerriegel
                 bleibt unangetastet - hier wird kein Gewerk abgehakt. */
              (darfAufmass || darfPruefen) && (
                <button key="b" onClick={() => setDialog('baustelle_fertig')}>
                  Baustelle fertig
                  <small>Draußen ist nichts mehr zu tun, Unterlagen folgen</small>
                </button>
              ),
              /* Der Knopf, an dem früher der ganze Aufmaßteil hing. Er
                 prüft die Pflichtordner, bevor er abhakt. */
              /* UND AUCH ER HING AN `aufmass.erfassen` ALLEIN.
                 ORIGINALTEXT MARVIN (07.09.2026, ein Vergleich zweier
                 Innendienstzugänge): „es fehlen zb in diesen beiden menüs
                 ... aber das sind alles punkte die der innendienst gebraucht
                 werden!" - Zweiter Fall nach dem Einblasstop (03.09.2026),
                 und der Weg im Server beschrieb den Fall `aufmass.pruefen`
                 seit Monaten in einem Zweig, den niemand betreten konnte.
                 Wer prüft, meldet auch fertig - mit Grund, und der steht
                 danach im Verlauf. */
              (darfAufmass || darfPruefen) && (
                <button key="f" onClick={() => setDialog('fertig')}>
                  Fertig melden
                  <small>Pflichtordner prüfen und abhaken</small>
                </button>
              ),
              /* DIE DRITTE ANTWORT AUF DIESELBE FRAGE.
                 ORIGINALTEXT MARVIN (08.09.2026): „außerdem braucht die
                 montage neben dem fertig melden und dem einblasstop auch die
                 möglichkeit über checkboxen bei «Auftrag nicht erledigt»
                 zusammen zu klicken, was sache ist, wie zb. keine luft auf
                 der pipe oder kein zugang zum Keller, oder kunde nicht vor
                 ort, damit der innendienst weiß was er damit machen soll."
                 Er steht deshalb NEBEN den beiden und hängt an denselben zwei
                 Rechten: wer fertig melden darf, darf auch sagen, dass es
                 nicht ging. */
              (darfAufmass || darfPruefen) && (
                <button key="n" onClick={() => setDialog('nicht_erledigt')}>
                  Nicht erledigt
                  <small>Ankreuzen, was der Innendienst wissen muss</small>
                </button>
              ),
              /* Vier Menüpunkte früher, hier ein Fenster: es ist dieselbe
                 Frage, nur zweimal. */
              darfPruefen && (
                <button key="d" onClick={() => setDialog('doku')}>
                  Freigeben
                  <small>Aufmaß oder Montage-Doku wieder öffnen</small>
                </button>
              ),
              darfRb && (
                <button key="r" onClick={() => setDialog('rb')}>
                  Rotberichtigung
                  <small>Die Nachweismappe zusammenstellen</small>
                </button>
              ),
            ]} />

            {/* WAS MAN AM AUFTRAG NACHSCHLAEGT ODER ANHAENGT. */}
            <Klappmenue titel="Werkzeuge" zahl={darfSchreiben ? Number(k.wfmt_ungelesen) || 0 : 0} kinder={[
              /* DER RUECKKANAL. Bis zum 26.08.2026 hing dieser Knopf an
                 `auftrag.schreiben`, und der Weg dahinter auch: ein Monteur
                 konnte im ganzen System nichts hinterlassen. Nicht
                 „Hausanschlussraum verschlossen", nicht „Kunde nicht da". Er
                 konnte anrufen. */
              /* UND ER STEHT NUR NOCH DA, WENN ES DEN ANDEREN WEG NICHT GIBT.
                 ORIGINALTEXT MARVIN (02.09.2026)
                   „bild 3: was ist das denn für eine kommentar funktion? wenn
                    es die bei der aufrtragsinfo ist, dann kann die da oben
                    raus."
                 Er hat recht: die Karte „Auftragsinfo" trägt seit dem
                 01.09.2026 den Chip „Eintrag schreiben", und der öffnet
                 DASSELBE Fenster. Zwei Wege zu derselben Sache sind kein
                 doppelter Komfort, sondern eine halbe Auskunft.
                 WEG IST ER TROTZDEM NICHT GANZ: wer den Block „Auftragsinfo"
                 nicht hat, sieht die Karte nicht - und für einen Monteur ist
                 das der einzige Rückweg ins Haus. Ein Rückkanal, den man dem
                 Empfänger nimmt, weil an anderer Stelle ein Chip steht, ist
                 keiner. */
              (darfSchreiben || darfMelden)
                && d.bloecke?.auftragsinfo === false && (
                <button key="k" onClick={() => setDialog('kommentar')}>
                  {darfSchreiben ? 'Kommentar' : 'Rückmeldung'}
                  <small>
                    {darfSchreiben ? 'Eine Zeile in die Auftragsinfo'
                                   : 'Was auf der Baustelle war, an den Innendienst'}
                  </small>
                </button>
              ),
              /* FÜR PARTNER FREIGEBEN (.278): sehen lassen ohne Termin. */
              darfSchreiben && (
                <button key="pf" onClick={() => setDialog('partner')}>
                  Für Partner freigeben
                  <small>Eine Firma sieht den Auftrag ohne Termin, jederzeit entziehbar</small>
                </button>
              ),
              darfSenden && (
                <button key="n" onClick={() => setDialog('nachricht')}>
                  Nachricht senden
                  <small>Telegram an eine Firma, Mail an den Kunden</small>
                </button>
              ),
              /* DIE MAIL MIT DATEIEN AUS DEM ORDNER (.332). Marvin,
                 07.10.2026: „eine email mit dateien, die bei uns in der
                 struktur liegen, zusammenklicken … direkt per mail zb an
                 die telekom senden, ohne dass ich sie erst herunterladen
                 muss." */
              darfSenden && (
                <button key="m" onClick={() => setDialog('mail')}>
                  Mail mit Dateien
                  <small>An Telekom, Gemeinde oder Firma, Anhänge aus dem Ordner</small>
                </button>
              ),
              /* Neun Werte, die im Deckblatt stehen und bis zum 24.08.2026 von
                 Hand abgetippt wurden. Das AUSLESEN ist Innendienstsache; die
                 Bauakte selbst liegt im Reiter „Dateien" und darf der Sub
                 sehen: er baut danach. */
              darfSchreiben && (
                <button key="b" onClick={() => setDialog('bauakte')}>
                  Bauakte
                  <small>Das Deckblatt auslesen</small>
                </button>
              ),
              darfSchreiben && (
                <button key="w" onClick={() => setDialog('wfmt')} style={{ position: 'relative' }}>
                  WFMT-Verlauf
                  {Number(k.wfmt_ungelesen) > 0 && <span className="glockenzahl" style={{ top: 6, right: 10 }}><span className="glockenziffer">{k.wfmt_ungelesen}</span></span>}
                  <small>{Number(k.wfmt_ungelesen) > 0 ? `${k.wfmt_ungelesen} neue Einträge der Telekom` : 'Was das Vorsystem der Telekom meldet'}</small>
                </button>
              ),
            ]} />

            {/* DER KNOPF OBEN FÄNGT VORNE AN. Er ist der Weg ohne Frage
                dahinter; wer aus einem BLOCK kommt, landet auf dessen Reiter
                (siehe `zumBereich` in der Übersicht). */}
            {darfSchreiben && (
              <button className="knopf haupt"
                      onClick={() => { setBearbeitenBereich('anschrift');
                                       setDialog('bearbeiten'); }}>
                Bearbeiten
              </button>
            )}
          </div>
        )}
      </div>

      <div className="reiter">
        {reiter.map((r) => (
          <button key={r.schluessel} aria-current={gezeigt === r.schluessel}
                  onClick={() => setWo(r.schluessel)}>
            {r.name}
          </button>
        ))}
      </div>

      <div className="inhalt">
        <Fehlerkasten lage={fehler} />
        {k.wartet && k.wartegrund && (
          <div className="hinweis warnung">
            Dieser Auftrag wartet: {k.wartegrund}
            {k.wartet_seit && ` (seit ${datum(k.wartet_seit)})`}
          </div>
        )}

        {/* Er zeigt sich NUR, wenn die Adresse wirklich in einem
            Pollergebiet liegt. Ein Kasten, in dem meistens „nein" steht,
            wird nach zwei Wochen nicht mehr gelesen. */}
        <PollerHinweis auftragId={id} />
        {/* DER STOP IST VORBEI, WENN ALLE GEWERKE ERLEDIGT SIND (.384).
            ORIGINALTEXT MARVIN (16.10.2026, Im Rosengarten 35B): „wenn hier
            alle gewerke abgeschlossen sind dann kannst du oben die meldung mit
            dem einblasstop entfernen, das wurde ja dann behoben. die info
            reicht dann in der auftragsinfo." Der Kasten warnt vor etwas, das
            noch im Weg steht; ist Einblasen und Spleißen erledigt, steht
            nichts mehr im Weg. */}
        {k.einblasstop_meter != null && !((d.gewerke ?? []).filter((g: any) => g.art !== 'DK' && !g.storniert_am).length > 0
            && (d.gewerke ?? []).filter((g: any) => g.art !== 'DK' && !g.storniert_am).every((g: any) => g.erledigt)) && (
          <div className="hinweis warnung">
            <b>Einblasstop gemeldet:</b> {k.einblasstop_meter} m
            {k.einblasstop_seite ? `, ${k.einblasstop_seite}` : ''}
            {k.einblasstop_seit ? ` (seit ${datum(k.einblasstop_seit)})` : ''}.
            {' '}Die Nachweisordner sind freigestellt, das Einblasprotokoll
            bleibt Pflicht.
            {/* WARUM DER AUFTRAG STEHT UND WAS ALS NÄCHSTES KOMMT (.327).
                ORIGINALTEXT MARVIN (07.10.2026, Andernacher Str. 240A):
                „wieso stellt das system den auftragsstatus nicht um? tiefbau
                 wurde doch erledigt. … warum ist da keine abschlussmeldung?"
                Der Stop parkt den Auftrag nach seiner eigenen Regel vom
                02.10. (.281); nur stand das nirgends auf der Seite, und auch
                nicht, dass die Meldung an die Telekom noch fehlte. */}
            <div style={{ marginTop: 6 }}>
              {k.einblasstop_parkt
                ? 'Der Auftrag ist deshalb auf „unterbrochen" geparkt: Einblasen und Spleißen können erst fertig werden, wenn die Telekom die Stelle behoben hat und der Stop zurückgenommen ist. Bis dahin wird der Tiefbau als Teilaufmaß abgerechnet. '
                : ''}
              {k.einblasstop_an_telekom_am
                ? <>An die Telekom gemeldet am {datum(k.einblasstop_an_telekom_am)}.</>
                : <><b>Noch nicht an die Telekom gemeldet.</b>
                    {darfSenden && <> <button type="button" className="knopf klein" onClick={() => setDialog('einblasstop')}>Jetzt an die Telekom melden</button></>}
                  </>}
            </div>
          </div>
        )}

        {gezeigt === 'uebersicht' && (
          /* WAS MAN SIEHT, SOLL MAN AUCH ANFASSEN KOENNEN.
             Marvin, 25.08.2026: „ich will das menü etwas innovativer. das
             heißt ich will da klicken können, zb bei den gewerken oder den
             behördenpapieren."
             Die Uebersicht war bis heute rein lesend: wer in der Gewerketabelle
             „kein Termin" las, musste den Blick nach oben in ein Menue nehmen,
             es aufklappen und „Termin" waehlen. Jetzt fuehrt die Zeile selbst
             dorthin. Die Uebersicht bleibt trotzdem lesend - sie oeffnet nur,
             was ohnehin da ist, und entscheidet nichts. */
          <AkteUebersicht ich={ich} d={d} auftragId={id} laden={laden}
                          springe={(ziel) => {
                            /* WAS EIN DIALOG IST UND WAS EIN REITER, ENTSCHEIDET
                               HIER - die Übersicht soll das nicht wissen
                               müssen. `bearbeiten` und `kommentar` sind seit
                               dem 31.08.2026 dabei: die Chips in den Blöcken
                               Kunde, Technik, Verwaltung und Auftragsinfo
                               führen dorthin. */
                            if (ziel.startsWith('bearbeiten')) {
                              /* `bearbeiten:kunde` heisst: dasselbe Fenster,
                                 aber auf dem Reiter, aus dem der Chip kam. */
                              setBearbeitenBereich(
                                (ziel.split(':')[1] as any) ?? 'anschrift');
                              setDialog('bearbeiten');
                            } else if (ziel.startsWith('vorbereiten')) {
                              /* `vorbereiten:planung` heisst: dasselbe
                                 Fenster, aber auf der Seite, aus der der Chip
                                 kam. Dieselbe Bauart wie bei `bearbeiten:…` -
                                 die Übersicht sagt, worum es geht, und nicht,
                                 wie das Fenster gebaut ist. */
                              setVorbereitenSeite(
                                ziel === 'vorbereiten:planung' ? 3 : 1);
                              setDialog('vorbereiten');
                            } else if (ziel === 'termin'
                                || ziel === 'kommentar'
                                || ziel === 'nachfassen'
                                || ziel === 'technikmeldung'
                                || ziel === 'nicht_erledigt'
                                /* SEIT DEM 18.09.2026, siehe `Sprungziel`. */
                                || ziel === 'fertig'
                                || ziel === 'baustelle_fertig'
                                || ziel === 'wfmt') {
                              setDialog(ziel);
                            } else setWo(ziel as any);
                          }} />
        )}
        {gezeigt === 'aufmass'    && <Aufmass ich={ich} auftragId={id} />}
        {gezeigt === 'dateien'    && (
          <Dateien ich={ich} auftragId={id} gewerke={d.gewerke}
                   neuLaden={laden} />
        )}
        {gezeigt === 'behoerde'   && <Behoerdenmappe ich={ich} auftragId={id} />}
        {gezeigt === 'rb'         && (
          <Rotberichtigung ich={ich} auftragId={id} alsReiter fertig={laden} />
        )}
        {gezeigt === 'leb'        && (
          <Leb ich={ich} auftragId={id} springe={(z) => setWo(z as any)} />
        )}
      </div>

      {dialog === 'bearbeiten' && (
        <Bearbeiten
          d={d} zu={() => setDialog(null)} start={bearbeitenBereich}
          fertig={async () => { await laden(); setDialog(null); }}
          archivieren={() => setDialog('archivieren')}
        />
      )}
      {dialog === 'kommentar' && (
        <Kommentar auftragId={id} istSub={ich.istSub}
                   zu={() => setDialog(null)}
                   fertig={async () => { await laden(); setDialog(null); }} />
      )}
      {dialog === 'nachfassen' && (
        <Nachfassen auftragId={id} laeuft={d.kopf.nachfassen_seit}
                    zu={() => setDialog(null)}
                    fertig={async () => { await laden(); setDialog(null); }} />
      )}
      {dialog === 'termin' && (
        <TerminBuchen d={d} auftragId={id} zu={() => setDialog(null)}
                      fertig={async () => { await laden(); setDialog(null); }} />
      )}
      {dialog === 'vorbereiten' && (
        <Vorbereiten ich={ich} auftragId={id} zu={() => setDialog(null)}
                     start={vorbereitenSeite} fertig={laden} />
      )}
      {dialog === 'rb' && (
        <Rotberichtigung ich={ich} auftragId={id} zu={() => setDialog(null)}
                         fertig={laden} />
      )}
      {dialog === 'einblasstop' && (
        <EinblasstopDialog
          auftragId={id}
          darfSenden={darfSenden}
          darfMelden={darfAufmass || darfPruefen}
          baustelle={baustelle(k)}
          zu={() => setDialog(null)}
          fertig={async () => { await laden(); setDialog(null); }}
        />
      )}
      {dialog === 'doku' && (
        <DokuDialog
          auftragId={id}
          baustelle={baustelle(k)}
          zu={() => setDialog(null)} fertig={laden}
        />
      )}
      {dialog === 'wfmt' && (
        <WfmtDialog
          auftragId={id}
          baustelle={baustelle(k)}
          zu={() => setDialog(null)}
        />
      )}
      {dialog === 'bauakte' && (
        <BauakteDialog
          ich={ich} auftragId={id}
          baustelle={baustelle(k)}
          zu={() => setDialog(null)} fertig={laden}
        />
      )}
      {dialog === 'fertig' && (
        <FertigDialog
          ich={ich} auftragId={id}
          baustelle={baustelle(k)}
          zu={() => setDialog(null)}
          fertig={async () => { await laden(); setDialog(null); }}
        />
      )}
      {/* WAS VON DER BAUSTELLE ZURÜCKKOMMT.
          Beide laden hinterher die Akte neu: die Technikmeldung ändert den
          Block „Technik" UND die Karte „Tiefbau", „Nicht erledigt" den
          Bearbeitungsstand im Kopf. Ein Fenster, das zugeht und einen alten
          Stand stehenlässt, sieht aus, als hätte es nichts getan. */}
      {dialog === 'technikmeldung' && (
        <TechnikmeldungDialog
          auftragId={id}
          baustelle={baustelle(k)}
          zu={() => setDialog(null)}
          fertig={async () => { await laden(); setDialog(null); }}
        />
      )}
      {dialog === 'nicht_erledigt' && (
        <NichtErledigtDialog
          auftragId={id}
          baustelle={baustelle(k)}
          zu={() => setDialog(null)}
          fertig={async () => { await laden(); setDialog(null); }}
        />
      )}
      {dialog === 'baustelle_fertig' && (
        <BaustelleFertigDialog
          auftragId={id}
          baustelle={baustelle(k)}
          zu={() => setDialog(null)}
          fertig={async () => { await laden(); setDialog(null); }}
        />
      )}
      {dialog === 'partner' && (
        <PartnerfreigabeDialog auftragId={id} baustelle={baustelle(k)} zu={() => setDialog(null)} />
      )}
      {dialog === 'mail' && (
        <MailDialog auftragId={id} zu={() => setDialog(null)} fertig={() => { setDialog(null); laden(); }} />
      )}
      {dialog === 'nachricht' && (
        <NachrichtDialog
          auftragId={id}
          baustelle={baustelle(k)}
          zu={() => setDialog(null)}
        />
      )}
      {dialog === 'archivieren' && (
        <ArchivDialog auftragId={id} zu={() => setDialog(null)}
                      fertig={async () => { await laden(); setDialog(null); zurueck(); }} />
      )}
    </>
  );
}


// ============================================================ Bearbeiten

/* DIE BEREICHE, IN DIE DIESES FORMULAR ZERFÄLLT.
 *
 * ORIGINALTEXT MARVIN (31.08.2026, zu zwei Bildschirmfotos des Fensters)
 *   „hier sind zum bearbeiten verschiedene bereiche zusammengefasst bitte
 *    unterteile die zum bearbeiten/ändern in die einzelnen bereiche."
 *
 * AUF SEINEN BILDERN IST DAS FENSTER ZWEIMAL ZU SEHEN, EINMAL OBEN UND EINMAL
 * WEITERGEROLLT - und es ist immer noch nicht zu Ende. Sechsunddreissig Felder
 * aus zwei Tabellen, von der Hausnummer über den Status der Telekom bis zum
 * WFMT-Kommentar, in einer einzigen Rolle. Wer die Rufnummer des Kunden
 * berichtigen will, rollt an der Anschrift, den Kennungen, dem Auftragstyp und
 * dem ganzen Netz vorbei.
 *
 * ES WAR ZUM SCHLUSS AUCH NICHT MEHR EHRLICH. Die Übersicht hat seit dem
 * 31.08.2026 in jedem Block einen Chip („Kundendaten ändern", „Technik
 * ändern", „Verwaltung ändern") - und alle drei führten in DIESELBE Rolle, an
 * denselben Anfang. Ein Weg, der aus dem Kundenblock heraus beim Straßennamen
 * landet, ist kein Weg in den Kundenblock.
 *
 * SECHS BEREICHE, EINER SICHTBAR. Der Chip landet dort, wo der Wert steht:
 * „Kundendaten ändern" öffnet KUNDE, „Technik ändern" öffnet NETZ,
 * „Verwaltung ändern" öffnet AUFTRAG.
 *
 * WARUM REITER UND NICHT SECHS FENSTER. Adresse und Auftrag liegen in zwei
 * Tabellen mit je eigenem Stand, und beide werden in EINEM Zug gespeichert.
 * Sechs Fenster hiessen sechs Entwürfe, sechs Speicherknöpfe und sechs
 * Gelegenheiten, dass einer davon den Stand des anderen überholt. Der Entwurf
 * bleibt einer; **was sich hier ändert, ist die Sicht darauf und nicht die
 * Buchführung darunter.** Ein Wechsel des Reiters verliert deshalb nichts:
 * eine begonnene Änderung im Kunden steht noch da, wenn man aus dem Netz
 * zurückkommt - und der Reiter sagt mit einem Punkt, dass dort etwas liegt.
 */
type Bereich = 'anschrift' | 'kennungen' | 'auftrag' | 'netz' | 'kunde' | 'stand';

/* DIE REITER HEISSEN WIE DIE KARTEN DER AKTE.
 *
 * ORIGINALTEXT MARVIN (02.09.2026)
 *   „benenne die tabs so, wie es auch im auftrag heißt, sonst suchst du die
 *    punkte immer"
 *
 * Hier stand „Anschrift", „Netz und Haus" und „Auftrag" - in der Akte heissen
 * dieselben Angaben „Baustelle", „Technik" und „Verwaltung". Wer die
 * Röhrchenfarbe berichtigen will, sucht sie unter dem Namen, unter dem er sie
 * gerade gelesen hat. **Zwei Namen für dieselbe Sache sind kein Komfort,
 * sondern eine Suche.**
 *
 * Zwei Reiter tragen keinen Kartennamen, weil es die Karte nicht gibt: die
 * KENNUNGEN stehen in der Akte verteilt (KLS und SM in „Baustelle"), und der
 * BEARBEITUNGSSTAND ist die Spalte, nach der die Arbeitsliste filtert. Sie
 * heissen deshalb nach dem, was darin steht, und nicht nach einem Ort, den es
 * nicht gibt. */
const BEREICHE: { schluessel: Bereich; titel: string }[] = [
  { schluessel: 'anschrift', titel: 'Baustelle' },
  { schluessel: 'kennungen', titel: 'Kennungen' },
  { schluessel: 'auftrag',   titel: 'Verwaltung' },
  { schluessel: 'netz',      titel: 'Technik' },
  { schluessel: 'kunde',     titel: 'Kunde' },
  { schluessel: 'stand',     titel: 'Bearbeitungsstand' },
];

/* WELCHES FELD ZU WELCHEM BEREICH GEHÖRT.
 *
 * Die Liste steht EINMAL da und wird für zwei Dinge gebraucht: für den Punkt
 * am Reiter („hier liegt eine ungespeicherte Änderung") und für den Sprung zum
 * Fehler. Weist der Server ein Feld ab, nennt er es beim Namen
 * (`Fachfehler.feld`) - und dann muss das Fenster den Reiter aufmachen, auf
 * dem dieses Feld steht. **Eine rote Meldung über einem Feld, das man nicht
 * sieht, ist schlimmer als keine Meldung:** sie sagt, dass etwas nicht stimmt,
 * und verschweigt wo. */
const FELD_BEREICH: Record<string, Bereich> = {
  strasse: 'anschrift', hausnummer: 'anschrift', hausnummer_zusatz: 'anschrift',
  plz: 'anschrift', ort_text: 'anschrift', notiz: 'anschrift',
  sm_nummer: 'kennungen', kls_id: 'kennungen', belegnummer: 'kennungen',
  belegposition: 'kennungen', auftragsnummer_ag: 'kennungen',
  auftragstyp_mds: 'auftrag', prio: 'auftrag', bearbeitungsstand: 'auftrag',
  status_tk: 'auftrag', auftragstyp_tk: 'auftrag',
  tb_bereich: 'auftrag', kein_tiefbau: 'auftrag',
  kein_oeffentl_grund: 'auftrag', maengelbeseitigung: 'auftrag',
  nvt: 'netz', onkz: 'netz', rohrverband: 'netz', roehrchen: 'netz',
  snr_rohrverband_ist: 'netz', snr_roehrchen_ist: 'netz', snr_laenge_m: 'netz',
  snr_pipe_vorhanden: 'netz', kabel_im_haus: 'netz',
  einheiten_we: 'netz', einheiten_ge: 'netz', einheiten_sl: 'netz',
  kunde_name: 'kunde', kunde_telefon: 'kunde', kunde_mobil: 'kunde',
  kunde_email: 'kunde',
  wartet: 'stand', wartegrund: 'stand', wfmt_kommentar: 'stand',
};


/**
 * Die Stammangaben eines Auftrags.
 *
 * Adresse und Auftrag liegen in zwei Tabellen und werden auch in zwei
 * Schritten gespeichert. Beide fuehren ihren eigenen Stand mit: wer nur den
 * Kundennamen aendert, soll nicht daran scheitern, dass jemand anderes an der
 * Adresse gearbeitet hat.
 */
function Bearbeiten({ d, zu, fertig, archivieren, start }: any) {
  const k = d.kopf;
  /* WO DAS FENSTER AUFGEHT, SAGT DER WEG DORTHIN. Ohne Angabe die Anschrift -
     sie steht in der Übersicht ganz oben und ist die Antwort auf „welcher
     Auftrag ist das". */
  const [bereich, setBereich] = useState<Bereich>(start ?? 'anschrift');
  const a = useEntwurf<any>({
    kls_id: k.kls_id ?? '', sm_nummer: k.sm_nummer ?? '',
    /* DIE VORWAHL UND DIE DREI EINHEITENZAHLEN, seit dem 31.08.2026.
       Als LEERER TEXT und nicht als 0, wo nichts steht: eine 0 ist eine
       Feststellung („hier ist kein Geschäft"), ein leeres Feld eine Auskunft
       („wir wissen es nicht"). */
    onkz: k.onkz ?? '',
    einheiten_we: k.einheiten_we == null ? '' : String(k.einheiten_we),
    einheiten_ge: k.einheiten_ge == null ? '' : String(k.einheiten_ge),
    einheiten_sl: k.einheiten_sl == null ? '' : String(k.einheiten_sl),
    kunde_name: k.kunde_name ?? '', kunde_telefon: k.kunde_telefon ?? '',
    kunde_email: k.kunde_email ?? '', kunde_mobil: k.kunde_mobil ?? '',
    auftragstyp_mds: k.auftragstyp_mds ?? '', prio: k.prio ?? '',
    belegnummer: k.belegnummer ?? '',
    belegposition: k.belegposition ?? '',
    auftragsnummer_ag: k.auftragsnummer_ag ?? '',
    nvt: k.nvt ?? '',
    rohrverband: k.rohrverband ?? '', roehrchen: k.roehrchen ?? '',
    /* DIE ZWEI ISTWERTE STEHEN HIER NICHT MEHR IM ENTWURF. Der Server weist
       sie ab (`nurRueckmeldung`), und ein Entwurf, der ein Feld führt, das
       kein Formular zeigt, ist die stille Gelegenheit, es doch zu schicken. */
    snr_laenge_m: k.snr_laenge_m ?? '',
    bearbeitungsstand: k.bearbeitungsstand ?? '',
    tb_bereich: k.tb_bereich ?? '', kein_tiefbau: !!k.kein_tiefbau,
    kein_oeffentl_grund: !!k.kein_oeffentl_grund,
    maengelbeseitigung: !!k.maengelbeseitigung,
    kabel_im_haus: !!k.kabel_im_haus,
    snr_pipe_vorhanden: !!k.snr_pipe_vorhanden,
    wartet: !!k.wartet, wartegrund: k.wartegrund ?? '',
    wfmt_kommentar: k.wfmt_kommentar ?? '',
    updated_at: k.updated_at,
  });

  // Der Status der Telekom traegt mehrere Werte gleichzeitig. Er liegt neben
  // dem Entwurf, weil useEntwurf Werte einzeln vergleicht und eine Liste
  // dabei immer als geaendert gaelte.
  const [statusTk, setStatusTk] = useState<string[]>(k.status_tk ?? []);
  /* DERSELBE WEG WIE `status_tk` DANEBEN, und aus demselben Grund: eine
     Liste vergleicht sich nicht wie ein Feld. */
  const [typTk, setTypTk] = useState<string[]>(k.auftragstyp_tk ?? []);
  const statusGeaendert =
    statusTk.join('|') !== ((k.status_tk ?? []) as string[]).join('|');
  const typGeaendert =
    typTk.join('|') !== ((k.auftragstyp_tk ?? []) as string[]).join('|');

  const [listen, setListen] = useState<Auswahllisten | null>(null);
  useEffect(() => { auswahllisten().then(setListen).catch(() => {}); }, []);
  const ad = useEntwurf<any>({
    strasse: k.strasse ?? '', hausnummer: k.hausnummer ?? '',
    hausnummer_zusatz: k.hausnummer_zusatz ?? '', plz: k.plz ?? '',
    ort_text: k.ort_text ?? '', notiz: k.adresse_notiz ?? '',
    updated_at: k.adresse_updated_at ?? undefined,
  });

  const laeuft = a.laeuft || ad.laeuft;
  /* AUCH DER TYP DER TELEKOM (.240). Marvin: „in dem fall "BLUK" wurde
     ausgewählt. aber ich kann die änderungen nicht speichern." Hier fehlte
     `typGeaendert`: der Punkt am Reiter stand, der Knopf blieb gesperrt. */
  const geaendert = a.geaendert || ad.geaendert || statusGeaendert || typGeaendert;
  const fehler = a.fehler ?? ad.fehler;

  /* WO ETWAS OFFENES LIEGT. Gefragt werden die Entwürfe selbst - eine zweite
     Liste „was habe ich angefasst" liefe beim ersten neuen Feld auseinander.
     `status_tk` steht neben dem Entwurf (eine Liste vergleicht sich nicht
     feldweise) und wird deshalb einzeln dazugelegt. */
  const offeneBereiche = new Set<Bereich>();
  for (const feld of Object.keys(a.aenderungen())) {
    const b = FELD_BEREICH[feld]; if (b) offeneBereiche.add(b);
  }
  for (const feld of Object.keys(ad.aenderungen())) {
    const b = FELD_BEREICH[feld]; if (b) offeneBereiche.add(b);
  }
  if (statusGeaendert || typGeaendert) offeneBereiche.add('auftrag');

  async function speichern() {
    a.setLaeuft(true); a.setFehler(null); ad.setFehler(null);
    try {
      if (ad.geaendert) await api.adresseAendern(k.adresse_id, ad.aenderungen());
      if (a.geaendert || statusGeaendert || typGeaendert) {
        const w = a.aenderungen();
        if (statusGeaendert) w.status_tk = statusTk;
        if (typGeaendert) w.auftragstyp_tk = typTk;
        await api.auftragAendern(k.id, w);
      }
      await fertig();
    } catch (f) {
      const lage = ausFehler(f);
      // Das Feld entscheidet, an welchem Formular der Fehler haengt.
      if (['strasse', 'hausnummer', 'plz', 'ort_text'].includes(lage.feld ?? '')) {
        ad.setFehler(lage);
      } else a.setFehler(lage);
      /* UND ES ENTSCHEIDET, WELCHER REITER AUFGEHT. Der Server weist ein Feld
         ab und nennt es beim Namen; steht es auf einem Reiter, den gerade
         niemand sieht, faerbt sich dort etwas rot, das niemand sieht. */
      const hin = FELD_BEREICH[lage.feld ?? ''];
      if (hin) setBereich(hin);
    } finally { a.setLaeuft(false); }
  }

  if (!a.entwurf || !ad.entwurf) return null;

  return (
    <Dialog titel="Auftrag bearbeiten" laeuft={laeuft} zu={zu} breite={780}>
      {/* DIE LEISTE SAGT AUCH, WO ETWAS LIEGT.
        *
        * Ein Reiter mit einer offenen Änderung trägt einen Punkt. Ohne ihn
        * wäre der Wechsel gefährlich: man ändert die Rufnummer, geht ins Netz,
        * kommt zurück und weiss nicht mehr, ob die Änderung noch da ist. Der
        * Punkt beantwortet das, ohne dass man klicken muss - und der
        * Speichern-Knopf unten speichert ohnehin alle Bereiche zusammen. */}
      <div className="bereichsleiste" role="tablist">
        {BEREICHE.map((b) => (
          <button
            key={b.schluessel} type="button" role="tab"
            aria-selected={bereich === b.schluessel}
            className={'bereichsreiter' + (bereich === b.schluessel ? ' an' : '')}
            onClick={() => setBereich(b.schluessel)}
          >
            {b.titel}
            {offeneBereiche.has(b.schluessel) && (
              <span className="punkt" title="Hier liegt eine ungespeicherte Änderung." />
            )}
          </button>
        ))}
      </div>

      <Formular geaendert={geaendert} laeuft={laeuft} fehler={fehler}
                speichern={speichern} verwerfen={zu}>
        {/* EINE GRÖSSE, ÜBER ALLE REITER.
          *
          * ORIGINALTEXT MARVIN (02.09.2026)
          *   „außerdem bei dem Auftragbearbeiten springt das immer in der
          *    größe wenn ich den tab wechsel, das soll immer eine größe
          *    bleiben"
          *
          * Sechs Reiter mit vier bis vierzehn Feldern: das Fenster wuchs und
          * schrumpfte bei jedem Klick, und mit ihm sprangen die Reiterleiste
          * und der Speichern-Knopf unter dem Zeiger weg. Der Kasten hält
          * jetzt die Höhe des GRÖSSTEN Bereichs; die kurzen lassen unten Luft,
          * und das ist der Preis dafür, dass nichts mehr springt.
          *
          * AUF DEM TELEFON GILT DAS NICHT. Dort ist die feste Höhe kein
          * Gewinn, sondern eine leere Fläche, durch die man rollen muss - die
          * Regel steht deshalb in `stil.css` hinter einer Breitenbedingung. */}
        <div className="bereichsleib">
        {bereich === 'anschrift' && (
          <>
          {/* KEINE ÜBERSCHRIFT: sie steht auf dem Reiter. Zweimal dasselbe
              Wort untereinander ist keine Gliederung, sondern Lärm. */}
        <div className="feldreihe">
          <Text name="strasse" titel="Straße" pflicht breite={280}
                wert={ad.entwurf.strasse} setze={(v) => ad.setze('strasse', v)}
                fehler={ad.fehler} />
          <Text name="hausnummer" titel="Nr." pflicht breite={90}
                wert={ad.entwurf.hausnummer} setze={(v) => ad.setze('hausnummer', v)}
                fehler={ad.fehler} />
          <Text name="hausnummer_zusatz" titel="Zusatz" breite={90}
                wert={ad.entwurf.hausnummer_zusatz}
                setze={(v) => ad.setze('hausnummer_zusatz', v)} fehler={ad.fehler} />
          <Text name="plz" titel="PLZ" pflicht breite={100}
                wert={ad.entwurf.plz} setze={(v) => ad.setze('plz', v)}
                fehler={ad.fehler} />
          <Text name="ort_text" titel="Ort" pflicht breite={200}
                wert={ad.entwurf.ort_text} setze={(v) => ad.setze('ort_text', v)}
                fehler={ad.fehler} />
        </div>
        {k.gemeinde
          ? <p className="klein leise">
              Gemeinde: {k.gemeinde}
              {k.gemeinde_gepflegt === false && ' (Stammdaten unvollständig)'}
            </p>
          : <p className="klein leise">
              Zu dieser Adresse ist keine Gemeinde hinterlegt. Ohne sie geht kein
              Behördenpapier hinaus.
            </p>}

        {/* DIE NOTIZ STEHT BEI DER ANSCHRIFT, ZU DER SIE GEHÖRT.
            Sie lag unter „Stand", zwischen dem Wartegrund und dem
            WFMT-Kommentar - beides Angaben über den AUFTRAG. Diese hier hängt
            an der ADRESSE und gilt für jeden Auftrag an diesem Haus; sie wird
            auch über `adresseAendern` gespeichert. Ein Feld, das in einem
            fremden Bereich steht, wird dort gesucht, wo es hingehört, und
            nicht gefunden. */}
        <Text name="notiz" titel="Notiz zur Adresse" mehrzeilig
              wert={ad.entwurf.notiz} setze={(v) => ad.setze('notiz', v)}
              fehler={ad.fehler}
              hinweis="Gilt für die Anschrift, nicht für diesen einen Auftrag.
                       Zufahrt, Hund, Schlüssel beim Nachbarn." />
          </>
        )}

        {bereich === 'kennungen' && (
          <>
        <div className="feldreihe">
          <Text name="sm_nummer" titel="SM-Nummer" breite={160}
                wert={a.entwurf.sm_nummer} setze={(v) => a.setze('sm_nummer', v)}
                fehler={a.fehler}
                hinweis="Ohne sie ist der Auftrag im Portal nicht auffindbar." />
          <Text name="kls_id" titel="KLS-Nummer" breite={160}
                wert={a.entwurf.kls_id} setze={(v) => a.setze('kls_id', v)}
                fehler={a.fehler} />
          {/* DIE BELEGNUMMER KOMMT VOM SAMMELDRUCK DER ABRUFE.
              ORIGINALTEXT MARVIN (02.09.2026): „das was du hier verknüpft
              hast ist nicht die belegnummer, die belegnummer beginnt mit
              4200...."
              Sie steht in keiner der beiden Telekom-Dateien; hier stand
              deshalb bis .86 die „Auftragsnummer AG" aus dem BAFA-Bericht,
              also eine ganz andere Zahl. Beide stehen jetzt nebeneinander,
              jede mit ihrer Herkunft - dann ist beim Hinsehen zu erkennen,
              welche in die LEB-Datei geht. */}
          <Text name="belegnummer" titel="Belegnummer" breite={160}
                wert={a.entwurf.belegnummer}
                setze={(v) => a.setze('belegnummer', v)} fehler={a.fehler}
                hinweis="10 Ziffern, beginnt mit 42. Kommt vom Sammeldruck der Abrufe und geht so in die LEB-Datei." />
          {/* DIE POSITION IST FEST HINTERLEGT UND MUSS NICHT GETIPPT WERDEN.
              ORIGINALTEXT MARVIN (18.09.2026)
                „bei der eingabe der belegnummer ist der zusatz „00010"
                 standard. hinterlege ihn fest, so dass wir nur noch die
                 belegnummer eingeben müssen wenn was fehlt."
              `auftrag.belegposition` trägt seit Migration 001 den
              Standardwert 00010; das Feld steht leer da und meint damit
              dasselbe. Seit dem 18.09.2026 geht die Position auch WIRKLICH so
              in die CSV - vorher schrieb der Export dort immer die Konstante,
              egal was hier stand. */}
          <Text name="belegposition" titel="Belegposition" breite={120}
                wert={a.entwurf.belegposition}
                platzhalter="00010"
                setze={(v) => a.setze('belegposition', v)} fehler={a.fehler}
                hinweis="po_item der LEB-Datei. Leer heißt 00010 - das ist der Normalfall und muss nicht eingetippt werden. Nur ausfüllen, wenn die Telekom eine andere Position gemeldet hat." />
          <Text name="auftragsnummer_ag" titel="Auftragsnummer AG" breite={160}
                wert={a.entwurf.auftragsnummer_ag}
                setze={(v) => a.setze('auftragsnummer_ag', v)} fehler={a.fehler}
                hinweis="Aus dem BAFA-Bericht. NICHT die Belegnummer." />
        </div>

        {/* WORAN DER IMPORT DIESEN AUFTRAG WIEDERERKENNT.

            ORIGINALTEXT MARVIN (28.08.2026)
              „ja, wir gehen immer bei jedem auftrag über die BAFA-ID denn
               diese ist immer einduetig, egal ob die SM oder KLS da ist oder
               nicht."

            KEINE EINGABEFELDER. Diese Kennungen kommen aus den Dateien der
            Telekom und werden dort vergeben; wer sie hier ändern könnte,
            könnte einen Auftrag von seinen künftigen Lieferungen abschneiden,
            ohne dass es auffällt - der nächste Lauf legte ihn dann noch einmal
            an. Sie stehen zum Lesen da.

            UND SIE STEHEN ALLE DA, nicht nur die erste. In einem
            Mehrfamilienhaus hängen mehrere Bauaufträge an derselben Anschrift;
            bis zum 28.08.2026 fielen alle bis auf eine aus. */}
        {Array.isArray(k.kennungen) && (
          <>
            <p className="klein" style={{ marginTop: 10, marginBottom: 4 }}>
              <b>Kennungen der Telekom.</b> Daran wird dieser Auftrag beim
              Import wiedererkannt &ndash; nicht an seiner Anschrift.
            </p>
            {k.kennungen.length === 0 ? (
              <p className="klein leise">
                Noch keine. Solange hier nichts steht, wird dieser Auftrag nur
                über seine Anschrift wiedergefunden: eine anders geschriebene
                Straße legt ihn ein zweites Mal an. Die nächste Lieferung der
                Telekom trägt die Kennung nach.
              </p>
            ) : (
              <ul className="klein" style={{ margin: '0 0 4px 18px' }}>
                {k.kennungen.map((x: any, i: number) => (
                  <li key={i}>
                    {x.art === 'bafa' ? 'BA/FA-ID' : 'Bauauftrag-ID'}{' '}
                    <b>{x.wert}</b>
                    {x.zuerst_am && (
                      <span className="leise">, seit {datum(x.zuerst_am)}</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {k.kennungen.length > 1 && (
              <p className="klein leise">
                Mehrere Bauaufträge an dieser Anschrift &ndash; im
                Mehrfamilienhaus der Normalfall. Sie werden hier als ein
                Auftrag geführt.
              </p>
            )}
          </>
        )}
          </>
        )}

        {bereich === 'auftrag' && (
          <>
        {/* DAS DROPDOWN, DAS GEFEHLT HAT.
          *
          * ORIGINALTEXT MARVIN (23.08.2026)
          *   „zb. der auftragstyp steht bisher nur als text, des fehlt das
          *    dropdown menü und die funktion dahinter."
          *
          * Die Funktion dahinter: aus dem Auftragstyp folgt, WELCHE GEWERKE
          * unsere sind. Sie steht unter dem Feld, damit die Folge sichtbar
          * ist, bevor gespeichert wird. */}
        <div className="feldreihe">
          <Auswahl name="auftragstyp_mds" titel="Auftragstyp" breite={280}
                   wert={a.entwurf.auftragstyp_mds}
                   setze={(v) => a.setze('auftragstyp_mds', v)}
                   optionen={listen?.auftragstyp ?? KEINE} fehler={a.fehler}
                   leerText="nicht gesetzt"
                   hinweis={typHinweis(listen, a.entwurf.auftragstyp_mds)} />
          <Auswahl name="prio" titel="Priorität" breite={160}
                   wert={a.entwurf.prio} setze={(v) => a.setze('prio', v)}
                   optionen={listen?.prio ?? KEINE} fehler={a.fehler}
                   leerText="ohne" />
          {/* Hiess bis zum 23.08.2026 anders und war zugleich der
              Ordner, in dem der Auftragsordner lag. Marvin: „Drive-Status
              fällt hier ja nun komplett weg. den gibt es ja nicht mehr."
              Der Ordner ist weg, der Zustand bleibt. */}
          <Auswahl name="bearbeitungsstand" titel="Bearbeitungsstand" breite={180}
                   wert={a.entwurf.bearbeitungsstand}
                   setze={(v) => a.setze('bearbeitungsstand', v)}
                   optionen={listen?.bearbeitungsstand ?? KEINE} fehler={a.fehler}
                   leerText="ohne"
                   hinweis="Warten heißt: beim Sub fertig, bei uns noch nicht." />
        </div>

        {/* Mehrere Zustaende gleichzeitig, deshalb Haken. Die Reihenfolge
          * ist der Weg des Auftrags; früher stand dieselbe Kombination in
          * beiden Richtungen und galt als zwei verschiedene Zustaende. */}
        {/* DER AUFTRAGSTYP DER TELEKOM STEHT HIER, und bis zum 09.09.2026
            stand er nirgends. Marvin: „ich kann den Auftragstyp (Telekom)
            nirgendwo eintragen bzw ändern." Er hat im Reiter „Baustelle"
            gesucht, weil der Wert im gleichnamigen BLOCK der Akte steht -
            und dieser Reiter trägt nur Adressfelder. Der Wegweiser dorthin
            steht jetzt an der Karte. */}
        <HakenReihe titel="Auftrag Typ (Telekom)"
                    optionen={listen?.auftragstyp_tk ?? KEINE}
                    wert={typTk} setze={setTypTk}
                    hinweis="Die Rohangabe aus der BAFA-Datei. Nicht zu
                             verwechseln mit dem Auftragstyp darüber: DER
                             entscheidet, welche Gewerke unsere sind. Wird
                             beim nächsten Einlesen von dort überschrieben." />

        <HakenReihe titel="Status Telekom"
                    optionen={listen?.status_tk ?? KEINE}
                    wert={statusTk} setze={setStatusTk}
                    hinweis="Kommt aus der Telekom-Datei und wird beim nächsten
                             Einlesen von dort überschrieben." />

        <div className="feldreihe">
          <Auswahl name="tb_bereich" titel="Tiefbaubereich" breite={220}
                   wert={a.entwurf.tb_bereich} setze={(v) => a.setze('tb_bereich', v)}
                   optionen={listen?.tb_bereich ?? KEINE} fehler={a.fehler}
                   leerText="nicht entschieden"
                   hinweis="Entscheidet, ob eine eigene VAO nötig ist." />
          <div className="feld" style={{ width: 'auto' }}>
            <Haken name="kein_tiefbau" titel="Ohne Tiefbau"
                   wert={!!a.entwurf.kein_tiefbau}
                   setze={(v) => a.setze('kein_tiefbau', v)}
                   hinweis="Dann werden keine Tiefbaupositionen vorgeschlagen." />
            {/* „wer nicht gräbt, gräbt auch nicht öffentlich": der Haken wird
              * NICHT selbst gesetzt, sonst stünde dort ein Kreuz, das niemand
              * gesetzt hat und das sich nicht entfernen lässt. */}
            <Haken name="kein_oeffentl_grund" titel="Kein öffentlicher Grund"
                   wert={!!a.entwurf.kein_oeffentl_grund
                         || !!a.entwurf.kein_tiefbau}
                   gesperrt={!!a.entwurf.kein_tiefbau}
                   setze={(v) => a.setze('kein_oeffentl_grund', v)}
                   hinweis={a.entwurf.kein_tiefbau
                     ? 'Folgt „Ohne Tiefbau": wer nicht gräbt, gräbt auch nicht '
                       + 'öffentlich.'
                     : 'Befreit von AAZ und VAO.'} />
            {/* EIN KENNZEICHEN, KEIN AUFTRAGSTYP.
                ORIGINALTEXT MARVIN (02.09.2026)
                  „die aufträge müssen nur angelegt werden und als solches
                   gekennzeichnet werden durch uns."
                Der Auftragstyp daneben ist eine der fünf
                Telekom-Leistungspositionen; eine Mängelbeseitigung ist keine
                davon und bleibt dort leer - das heisst in diesem Haus „alle
                drei Gewerke". */}
            <Haken name="maengelbeseitigung" titel="Mängelbeseitigung"
                   wert={!!a.entwurf.maengelbeseitigung}
                   setze={(v) => a.setze('maengelbeseitigung', v)}
                   hinweis="Gleiche Anschrift wie ein früherer Auftrag, aber
                            eigene BA/FA-ID und eigene SM-Nummer. Er wird nicht
                            als Doppelgänger zusammengelegt." />
          </div>
        </div>

        {/* DIE EINHEITEN ZUM HAUS, IN DER ZÄHLWEISE DES AUFTRAGGEBERS.
          *
          * ORIGINALTEXT MARVIN (31.08.2026)
          *   „es sind Wohneinheiten(WE), Geschäftseinheiten (GE),
          *    Sonderleitungen (SL)"
          *
          * Drei Zahlen und keine Summe: eine Geschäftseinheit hat andere
          * Zugangszeiten als eine Wohnung, und eine Sonderleitung ist
          * überhaupt kein Anschluss im gewohnten Sinn. Die WE kommen aus der
          * Zahl der Bauauftrag-Kennungen (Migration 067), GE und SL trägt der
          * Innendienst nach - der Abruf sagt nicht, welche Zeile ein Geschäft
          * ist, und eine geratene Zahl ist schlimmer als eine leere. */}
          </>
        )}

        {bereich === 'netz' && (
          <>
          {/* EINHEITEN UND NETZ STEHEN IN EINEM BEREICH, weil sie in der
              Übersicht in EINER Karte stehen: „Technik". Der Chip dort führt
              hierher, und er soll nicht auf einen Reiter führen, auf dem die
              Hälfte dessen fehlt, was daneben stand. */}
        <h4 style={{ margin: '14px 0 4px' }}>Einheiten am Haus</h4>
        {/* .361: Marvin, 14.10.2026: „ausschließlich aus GBGS, weil die
            Datenblätter nicht valide sind". */}
        <p className="klein leise" style={{ margin: '0 0 6px' }}>
          Kommen aus dem GBGS und werden nach jedem GBGS-Lesen abgeglichen. Was hier von Hand
          steht, gilt nur, solange das GBGS zu diesem Auftrag nichts sagt.
        </p>
        <div className="feldreihe">
          <Text name="einheiten_we" titel="WE" breite={90}
                wert={a.entwurf.einheiten_we}
                setze={(v) => a.setze('einheiten_we', v)} fehler={a.fehler}
                hinweis="Wohneinheiten" />
          <Text name="einheiten_ge" titel="GE" breite={90}
                wert={a.entwurf.einheiten_ge}
                setze={(v) => a.setze('einheiten_ge', v)} fehler={a.fehler}
                hinweis="Geschäftseinheiten" />
          <Text name="einheiten_sl" titel="SL" breite={90}
                wert={a.entwurf.einheiten_sl}
                setze={(v) => a.setze('einheiten_sl', v)} fehler={a.fehler}
                hinweis="Sonderleitungen" />
        </div>

        <h4 style={{ margin: '14px 0 4px' }}>Netz</h4>
        <div className="feldreihe">
          <Text name="nvt" titel="NVt" breite={140}
                wert={a.entwurf.nvt} setze={(v) => a.setze('nvt', v)}
                fehler={a.fehler} />
          {/* DIE VORWAHL.
            *
            * ORIGINALTEXT MARVIN (31.08.2026)
            *   „außerdem fehlt bei der technik noch die ONKz und die
            *    Einheiten zum Haus"
            *
            * Sie kommt aus dem Abruf, aus der BAFA und seit .53 aus der
            * Bauakte - alle drei können sie nicht haben, und dann trägt sie
            * jemand von Hand nach. */}
          <Text name="onkz" titel="ONKz (Vorwahl)" breite={130}
                wert={a.entwurf.onkz} setze={(v) => a.setze('onkz', v)}
                fehler={a.fehler}
                hinweis="Mit führender Null, zum Beispiel 0261." />
          {/* ORIGINALTEXT MARVIN (30.07.2026)
            *   „dann will ich überall haben wo es hinterlegt ist, dass der
            *    rohrverband und die röhrchen als dropdown sind."
            *
            * Die Liste fuehrt die Hand, sie sperrt nicht die Tuer: ein Wert
            * daneben wird angenommen und im Befund gemeldet. Der
            * Bauakte-Scan schreibt diese Spalten automatisch. */}
          <Auswahl name="rohrverband" titel="Rohrverband (Plan)" breite={190}
                   wert={a.entwurf.rohrverband}
                   setze={(v) => a.setze('rohrverband', v)}
                   optionen={listen?.rohrverband ?? KEINE} fehler={a.fehler}
                   leerText="ohne" />
          <Auswahl name="roehrchen" titel="Röhrchen (Plan)" breite={140}
                   wert={a.entwurf.roehrchen}
                   setze={(v) => a.setze('roehrchen', v)}
                   optionen={listen?.roehrchen ?? KEINE} fehler={a.fehler}
                   leerText="ohne" />
        </div>
        {/* WAS VOR ORT VORGEFUNDEN WIRD, STEHT HIER NICHT MEHR ZUM TIPPEN.
         *
         * ORIGINALTEXT MARVIN (08.09.2026)
         *   „hier geht es um die rückmeldungen die der tiefbau draußen macht
         *    und notwendig sind, dass er sie bei sich im auftrag einträgt und
         *    anpasst, diese infos sollen dann ja für alle anderen sichtbar
         *    sein"
         *
         * Bis .137 standen „Rohrverband (Ist)" und „Röhrchen (Ist)" als zwei
         * Auswahlfelder in DIESEM Formular. Seit Migration 188 tragen die
         * Istwerte ihren Stempel - wer sie wann gemeldet hat -, und die Akte
         * schreibt genau das hin. **Ein zweiter Schreiber ohne Stempel macht
         * aus dieser Auskunft eine falsche.** Der Weg dorthin steht daneben;
         * der Server weist die beiden Felder ab und nennt ihn ebenfalls.
         *
         * Die geplanten Werte bleiben hier: sie stammen aus der Bauakte und
         * werden von ihrem Scan überschrieben - das ist genau der Grund,
         * warum es die Istspalten überhaupt gibt. */}
        <div className="hinweis">
          <b>Was vor Ort abweicht, wird zurückgemeldet.</b> SNR-Länge,
          Rohrverband, Röhrchen und Rohrstärke nimmt die Karte „Tiefbau" in
          der Übersicht entgegen, Knopf „Zurückmelden". Dort steht danach,
          wer die Angabe wann gemeldet hat, und die Partnerfirma sieht sie
          in der Auftragsinfo. Hier stehen die Werte der <b>Planung</b>.
        </div>
        <div className="feldreihe">
          <Text name="snr_laenge_m" titel="SNR-Länge in m (Planung)"
                art="number" breite={190}
                wert={String(a.entwurf.snr_laenge_m ?? '')}
                setze={(v) => a.setze('snr_laenge_m', v)} fehler={a.fehler} />
          <div className="feld" style={{ width: 'auto' }}>
            <Haken name="snr_pipe_vorhanden" titel="SNR-Pipe vorhanden"
                   wert={!!a.entwurf.snr_pipe_vorhanden}
                   setze={(v) => a.setze('snr_pipe_vorhanden', v)} />
            <Haken name="kabel_im_haus" titel="Kabel im Haus"
                   wert={!!a.entwurf.kabel_im_haus}
                   setze={(v) => a.setze('kabel_im_haus', v)} />
          </div>
        </div>
          </>
        )}

        {bereich === 'kunde' && (
          <>
        <div className="feldreihe">
          <Text name="kunde_name" titel="Name" breite={260}
                wert={a.entwurf.kunde_name} setze={(v) => a.setze('kunde_name', v)}
                fehler={a.fehler} />
          <Text name="kunde_telefon" titel="Telefon" art="tel" breite={170}
                wert={a.entwurf.kunde_telefon}
                setze={(v) => a.setze('kunde_telefon', v)} fehler={a.fehler} />
          <Text name="kunde_mobil" titel="Mobil" art="tel" breite={170}
                wert={a.entwurf.kunde_mobil}
                setze={(v) => a.setze('kunde_mobil', v)} fehler={a.fehler} />
          <Text name="kunde_email" titel="E-Mail" art="email" breite={240}
                wert={a.entwurf.kunde_email}
                setze={(v) => a.setze('kunde_email', v)} fehler={a.fehler} />
        </div>
          </>
        )}

        {bereich === 'stand' && (
          <>
        <Haken name="wartet" titel="Dieser Auftrag wartet"
               wert={!!a.entwurf.wartet} setze={(v) => a.setze('wartet', v)}
               hinweis="Ein wartender Auftrag ohne Grund ist ein Auftrag, den
                        niemand wieder anfasst." />
        {a.entwurf.wartet && (
          <Text name="wartegrund" titel="Worauf wartet er?" pflicht mehrzeilig wert={a.entwurf.wartegrund}
                setze={(v) => a.setze('wartegrund', v)} fehler={a.fehler} />
        )}
        <Text name="wfmt_kommentar" titel="Kommentar aus WFMT" mehrzeilig wert={a.entwurf.wfmt_kommentar}
              setze={(v) => a.setze('wfmt_kommentar', v)} fehler={a.fehler} />
          </>
        )}
        </div>
      </Formular>

      {/* DAS ARCHIVIEREN BLEIBT UNTER ALLEN BEREICHEN, nicht in einem.
        *
        * Es ändert kein Feld, sondern nimmt den ganzen Auftrag aus den Listen
        * - das gehört unter den Strich und nicht neben eine Rufnummer. Und es
        * bleibt sichtbar, egal welcher Reiter offen ist: ein Knopf, den man
        * nur auf einem von sechs Reitern findet, ist ein Knopf, den man
        * sucht. */}
      <div style={{ marginTop: 18, borderTop: '1px solid var(--rand,#e5e7eb)',
                    paddingTop: 10 }}>
        <button className="knopf" onClick={archivieren} disabled={laeuft}>
          Auftrag archivieren
        </button>
        <span className="klein leise" style={{ marginLeft: 10 }}>
          Archiviert heißt: aus den Listen heraus, nicht gelöscht.
        </span>
      </div>
    </Dialog>
  );
}


/**
 * Ein Kommentar am Auftrag - und für den Sub der einzige Rückweg.
 *
 * FÜR IHN GIBT ES KEIN GEWERK MEHR (31.08.2026).
 *
 * ORIGINALTEXT MARVIN
 *   „wenn der partner einen kommentar schreibt, dann gibt es kein gewerk mehr
 *    zur trennung. das system ist doch vollkommen anders aufgebaut."
 *
 * HIER STAND EIN PFLICHTFELD „Gewerk" mit „Einblasen" und „Spleissen" darin,
 * und der Monteur sollte entscheiden, zu welchem der beiden
 * „Hausanschlussraum verschlossen" gehört. **Es gehört zu beiden.** Ein
 * Pflichtfeld, dessen richtige Antwort „beides" ist, ist kein Pflichtfeld,
 * sondern eine Hürde - und sie stand vor dem einzigen Rückweg, den er hat.
 *
 * Die Trennung nach Gewerk kam von früher: dort gab es je Gewerk eine
 * Kommentarspalte, weil eine Zeile früher nur so viele Spalten hat, wie
 * jemand angelegt hat. Hier gibt es die AUFTRAGSINFO, und dort steht seine
 * Meldung jetzt - mit Namen, Datum und einer Berichtigung, die ihre eigene
 * Spur hinterlässt.
 *
 * FÜR DEN INNENDIENST BLEIBT DIE WAHL. Er schreibt gelegentlich an EINE
 * Buchung („Bagger kommt erst nachmittags"), und dort ist das Gewerk die
 * Sache selbst und keine Trennung.
 */
function Kommentar({ auftragId, istSub, zu, fertig }: any) {
  /* KEIN GEWERK MEHR, FÜR NIEMANDEN (31.08.2026).
   *
   * ORIGINALTEXT MARVIN
   *   „ich brauch hier keine kommentare in der AI für die gewerke, wir tragen
   *    sie für alle ein."
   *
   * Am Vormittag war das Feld für den PARTNER gefallen („wenn der partner
   * einen kommentar schreibt, dann gibt es kein gewerk mehr zur trennung"),
   * für den Innendienst blieb die Wahl stehen. Auf seinem Bildschirmfoto steht
   * sie aufgeklappt da: „ohne Gewerk / Tiefbau / Einblasen" - und die richtige
   * Antwort ist immer die erste. **Ein Auswahlfeld, dessen sinnvolle Antwort
   * immer dieselbe ist, ist kein Feld, sondern ein Handgriff.**
   *
   * Es ist auch keine Auskunft, die verlorengeht: was ein Mensch schreibt,
   * gehört zum AUFTRAG. „Hausmeister anrufen: 0174 6387918" gilt für den
   * Tiefbauer wie für den Monteur; wer es an ein Gewerk hängt, versteckt es
   * vor dem anderen. Die Auftragsinfo trägt es für alle - mit Namen, Datum
   * und einer Berichtigung, die ihre eigene Spur hinterlässt. */
  const [text, setText] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  return (
    <Dialog titel={istSub ? 'Rückmeldung von der Baustelle'
                          : 'Kommentar zum Auftrag'}
            laeuft={laeuft} zu={zu} breite={560}>
      <Fehlerkasten lage={fehler} />
      {istSub && (
        <p className="klein leise" style={{ marginTop: 0 }}>
          Der Innendienst liest das am Auftrag. Was hier steht, steht auch für
          Sie in der Auftragsinfo.
        </p>
      )}
      <Text name="text" titel="Text" pflicht mehrzeilig
            wert={text} setze={setText} fehler={fehler}
            platzhalter={istSub
              ? 'Hausanschlussraum verschlossen, Kunde nicht angetroffen'
              : undefined} />
      <div className="reihe" style={{ marginTop: 10 }}>
        <button className="knopf haupt"
                disabled={laeuft || text.trim().length < 2}
                onClick={async () => {
                  setLaeuft(true); setFehler(null);
                  try {
                    await api.auftragKommentar(auftragId, text);
                    await fertig();
                  }
                  catch (f) { setFehler(ausFehler(f)); }
                  finally { setLaeuft(false); }
                }}>
          {laeuft ? 'Einen Moment' : istSub ? 'Melden' : 'Eintragen'}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}


function ArchivDialog({ auftragId, zu, fertig }: any) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  return (
    <MitGrund
      titel="Auftrag archivieren" knopf="Archivieren"
      erklaerung="Der Auftrag verschwindet aus den Arbeitslisten. Nichts wird
                  gelöscht, und der Grund steht danach im Verlauf."
      laeuft={laeuft} fehler={fehler} zu={zu}
      fertig={async (g) => {
        setLaeuft(true); setFehler(null);
        try { await api.auftragArchivieren(auftragId, g); await fertig(); }
        catch (f) { setFehler(ausFehler(f)); }
        finally { setLaeuft(false); }
      }}
    />
  );
}


// ============================================================== Termin

/**
 * Einen Termin vergeben.
 *
 * Der Bildschirm fragt VOR dem Buchen: /terminpruefung sagt, was dagegen
 * spricht, und zwar mit Grund. Ein Termin, der ohne Erklaerung abgelehnt
 * wird, wird beim naechsten Versuch genauso abgelehnt.
 */
/**
 * „WIR HABEN DEN KUNDEN NICHT ERREICHT" - setzen oder beenden.
 *
 * DER GRUND IST PFLICHT, wenn es ANGEHT, und beim Beenden nicht. Ein Datum
 * ohne Satz ist am fälligen Tag keine Hilfe; „erledigt" braucht keine
 * Begründung - dieselbe Regel wie beim Änderungsverzeichnis und bei der
 * Wiedervorlage.
 */
function Nachfassen({ auftragId, laeuft: schonAn, zu, fertig }: any) {
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const an = !schonAn;

  return (
    <Dialog titel={an ? 'Kunde nicht erreicht' : 'Nachfassen beenden'}
            laeuft={laeuft} zu={zu} breite={560}>
      <Fehlerkasten lage={fehler} />
      {an ? (
        <>
          <p className="klein">
            Der Auftrag steht danach auf <b>wartet</b>, und der Kunde bekommt
            die Erinnerungen aus der Konfiguration - werktags, im eingestellten
            Abstand. Die erste Terminvergabe beendet das von selbst.
          </p>
          <Text name="grund" titel="Was war beim Anruf?" pflicht mehrzeilig
                wert={grund} setze={setGrund} fehler={fehler}
                hinweis="Steht in der Auftragsinfo. Am fälligen Tag steht sonst
                         nur ein Datum da." />
        </>
      ) : (
        <p className="klein">
          Es gehen keine weiteren Erinnerungen an den Kunden. Der
          Bearbeitungsstand bleibt, wie er ist - ihn ändert nur ein Mensch.
        </p>
      )}
      <div className="reihe" style={{ marginTop: 12 }}>
        <button className="knopf haupt"
                disabled={laeuft || (an && !grund.trim())}
                onClick={async () => {
                  setLaeuft(true); setFehler(null);
                  try {
                    await api.nachfassen(auftragId, { an, grund });
                    await fertig();
                  } catch (f) { setFehler(ausFehler(f)); }
                  finally { setLaeuft(false); }
                }}>
          {laeuft ? 'Einen Moment'
                  : an ? 'Nachfassen starten' : 'Nachfassen beenden'}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>
    </Dialog>
  );
}

function TerminBuchen({ d, auftragId, zu, fertig }: any) {
  const k = d.kopf;
  const [gewerk, setGewerk] = useState('TB');
  const [sub, setSub] = useState('');
  const [bereich, setBereich] = useState(k.tb_bereich ?? '');
  const [auchPartner, setAuchPartner] = useState(false);
  /* DIE GEWÄHLTEN TAGE MIT IHREN ZEITEN. Sie sind das, was gebucht wird -
     einer bei einem Tagestermin, mehrere bei einem mehrtägigen Tiefbau. Ein
     Datumsfeld und zwei Uhrzeitfelder standen hier bis .90; sie beantworteten
     die Frage nicht, mit der jemand dieses Fenster aufmacht. */
  const [tage, setTage] = useState<Bautag[]>([]);
  /* .507: DER VORBELEGTE TAG IST DER ALTE TERMIN, KEIN GEWÄHLTER.
     ORIGINALTEXT MARVIN (27.10.2026, Krüsmannstr. 22)
       „Der Termin sollte umgebucht werden vom 12.11. auf den 16.11. ... Im
        Kunden ist es aber immer noch der 12. In der Wochenplanung ist es als
        zweiter Tag hinterlegt. Und die vermeintliche Kundenmail ... ist gar
        nicht rausgegangen."
     Beim Tiefbau hängt der Kalender einen angeklickten Tag an die Auswahl an
     (mehrtägig). Mit dem vorbelegten 12.11. wurde aus dem Klick auf den
     16.11. ein Zweitagesblock 12.11. und 16.11.: der Termin blieb am 12.11.,
     also keine Umbuchung, keine Mail. Jetzt gilt: solange nur der vorbelegte
     Tag steht, ERSETZT der erste Klick auf einen anderen Tag ihn. Wer den
     Tiefbau verlängern will, nimmt die Knöpfe „1,5 Tage", „2 Tage" oder klickt
     danach weitere Tage an. (.440 hatte dasselbe im Terminplaner behoben.) */
  const [vorbelegt, setVorbelegt] = useState<string | null>(null);
  const tageSetzen = (neu: Bautag[]) => {
    setTage(neu);
    if (!(neu.length === 1 && neu[0].iso === vorbelegt)) setVorbelegt(null);
  };
  const [monat, setMonat] = useState(() => heuteIso().slice(0, 7));
  const [subs, setSubs] = useState<any[]>([]);
  const [p, setP] = useState<any>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [listen, setListen] = useState<Auswahllisten | null>(null);
  /* WAS NACH DER BUCHUNG NOCH ZU FRAGEN IST. Der Server sagt es (`nachfrage`);
     hier steht nur der Ablauf. */
  const [nachfrage, setNachfrage] = useState<any>(null);

  useEffect(() => { api.subunternehmer().then(setSubs).catch(() => {}); }, []);
  useEffect(() => { auswahllisten().then(setListen).catch(() => {}); }, []);

  // Die Pruefung laeuft VOR dem Buchen und bei jeder Aenderung mit. Ein
  // Termin, der erst beim Klick abgelehnt wird, kostet einen Anlauf.
  const tag = tage.length ? tage[0].iso : '';
  useEffect(() => {
    const q = new URLSearchParams({ gewerk, monat });
    if (tag) q.set('datum', tag);
    if (bereich) q.set('bereich', bereich);
    api.terminpruefung(auftragId, q).then(setP).catch(() => setP(null));
  }, [gewerk, tag, bereich, monat, auftragId]);

  /* EINEN MEHRTÄGIGEN TERMIN GIBT ES NUR BEIM TIEFBAU. Wer das Gewerk
     wechselt, nimmt seine Folgetage nicht mit: sonst stünden bei „Einblasen"
     drei Tage, die der Server beim Buchen stillschweigend auf einen
     eindampft. */
  useEffect(() => {
    if (p && p.mehrtagErlaubt === false && tage.length > 1) setTage([tage[0]]);
  }, [p?.mehrtagErlaubt]);

  /* WAS SCHON GEBUCHT IST, STEHT BEIM ÖFFNEN DA.
     ------------------------------------------------------------------------
     Tag, Zeit und Firma des bestehenden Termins sind vorbelegt - dann sieht
     man im Kalender, WO er liegt, und verschiebt ihn von dort. Ohne das fängt
     jede Umbuchung bei einem leeren Kalender an, und der bestehende Termin
     ist nur eine Zeile weiter unten.

     NUR BEIM ÖFFNEN UND BEIM WECHSEL DES GEWERKS. Wer den Tag danach
     umstellt, soll ihn nicht wieder zurückgesetzt bekommen. */
  useEffect(() => {
    const g = (d.gewerke ?? []).find(
      (x: any) => x.art === gewerk && x.termin_datum && !x.storniert_am);
    if (!g) { setTage([]); setVorbelegt(null); return; }
    const iso = String(g.termin_datum).slice(0, 10);
    setTage([{ iso,
               von: String(g.termin_von ?? '07:00').slice(0, 5),
               bis: String(g.termin_bis ?? '16:00').slice(0, 5) }]);
    setVorbelegt(iso);
    setMonat(iso.slice(0, 7));
    if (g.firma_id) setSub(g.firma_id);
  }, [gewerk]);

  /* EINE FIRMA, DIE NICHT MEHR ZUR WAHL STEHT, BLEIBT NICHT GEWÄHLT.
     Wer beim Einblasen eine reine Einblasfirma wählt und dann auf „Einblasen +
     Spleißen" umstellt, hätte sonst eine Firma im Feld, die in der Liste
     darunter gar nicht mehr vorkommt - und der Server nähme sie an. Das Feld
     wird geleert, nicht geraten: welche Firma statt dessen fährt, entscheidet
     der Innendienst. */
  useEffect(() => {
    if (sub && subs.length && !passende.some((s: any) => s.id === sub)) setSub('');
  }, [gewerk, auchPartner, subs.length]);

  /* DER KALENDER SPRINGT AUF DEN MONAT, IN DEM ETWAS GEWÄHLT IST. Ohne das
     steht der gewählte Tag zwar in der Leiste und im Kalender nicht - und
     dann sieht es aus, als wäre die Auswahl weg. */
  useEffect(() => {
    if (tag && tag.slice(0, 7) !== monat) setMonat(tag.slice(0, 7));
  }, [tag]);

  /* GIBT ES DIESEN TERMIN SCHON? DANN IST ES EINE UMBUCHUNG.
     ------------------------------------------------------------------------
     ORIGINALTEXT MARVIN (03.09.2026, mit Bildschirmfoto)
       „es ist nicht erkennbar, dass es sich um eine umbuchung handeln würde.
        das musst du ausbessern."

     ER HAT RECHT, UND ES WAR MEHR ALS EINE AUFSCHRIFT. Das Fenster hiess
     „Termin vergeben", der Knopf „Buchen", und der bestehende Termin stand
     ganz unten unter „Schon vergeben" - drei Bildschirmhöhen entfernt und
     ohne Zusammenhang zu dem, was man gerade tut. **Wer nicht sieht, dass er
     etwas VERSCHIEBT, verschiebt es versehentlich.**

     Der Bestand hat es genauso gehalten: dort hiess der Knopf „Termin
     übernehmen", solange nichts stand, und „Termin umbuchen", sobald etwas
     stand - und „Firma wechseln", wenn nur sie sich ändert. */
  /* WAS GEBUCHT WIRD: EIN GEWERK ODER DIE GEKOPPELTE MONTAGE.
     `gewerk` bleibt eines von drei - der Server kennt kein „EBSP". `kombi`
     sagt, dass der Partner mitgebucht wird, `arten` sagt, WELCHE Gewerke das
     am Ende sind. Beides steht hier EINMAL: „bestehend", die Firmenauswahl und
     der Buchungsaufruf fragen dieselbe Rechnung, statt jeder für sich
     `gewerk === 'EB' && auchPartner` nachzubauen. */
  const kombi = auchPartner && (gewerk === 'EB' || gewerk === 'SP');
  const arten: string[] = kombi
    ? [gewerk, gewerk === 'EB' ? 'SP' : 'EB'] : [gewerk];

  /* UND „SCHON VERGEBEN" GILT FÜR JEDE ART, DIE GEBUCHT WIRD. Wer „Einblasen +
     Spleißen" wählt und nur beim Spleißen einen Termin stehen hat, VERSCHIEBT
     ihn - das Fenster hiesse sonst „Termin vergeben", und der bestehende
     Spleisstermin wäre still ersetzt. */
  const bestehende = arten
    .map((art) => (d.gewerke ?? []).find(
      (g: any) => g.art === art && g.termin_datum && !g.storniert_am))
    .filter(Boolean);
  const bestehend = bestehende[0];
  const firmaWechsel = !!bestehend && !!sub && !!bestehend.firma_id
                       && bestehend.firma_id !== sub;

  const gesperrt = p && !p.riegel?.erlaubt;
  /* NUR FIRMEN, DIE ALLES FÜHREN, WAS GEBUCHT WIRD.
     Bei „Einblasen + Spleißen" bekommen BEIDE Gewerke dieselbe Firma - eine,
     die nur einblast, stünde sonst zur Wahl und wäre danach fürs Spleißen
     eingetragen. Eine Firma OHNE hinterlegte Gewerke bleibt überall wählbar:
     eine leere Liste heisst in diesem Haus „alle" und nicht „keines". */
  const passende = subs.filter((s: any) =>
    s.aktiv && (!s.gewerke?.length || arten.every((w) => s.gewerke.includes(w))));

  // Die Regelzeit haengt am Gewerk: Tiefbau faengt frueher an als Montage.
  // Sie kommt aus der Pruefung, nicht aus einer zweiten Liste hier.
  const fenster = p?.fenster;
  /* WER DAS GEWERK WECHSELT, WECHSELT DAS ZEITFENSTER. Ein Tiefbautag von
     07:00 bis 16:00, mit „Einblasen" darüber, wäre eine Zeit, die diese
     Regelzeit gar nicht kennt. Die TAGE bleiben stehen - nur ihre Zeiten
     ziehen nach. */
  useEffect(() => {
    if (!fenster?.ganz || !tage.length) return;
    setTage(tage.map((t) => ({ ...t, von: fenster.ganz.von, bis: fenster.ganz.bis })));
  }, [fenster?.ganz?.von, fenster?.ganz?.bis]);

  /* DIE RÜCKFRAGEN LÖSEN DAS BUCHUNGSFENSTER AB. Zwei Fenster übereinander
     wären zwei Schliessknöpfe, und der falsche schliesst beide. */
  if (nachfrage) {
    return (
      <Nachfragen auftragId={auftragId} nachfrage={nachfrage}
                  baustelle={baustelle(k)}
                  fertig={() => { setNachfrage(null); fertig(); }} />
    );
  }

  return (
    /* EIN FENSTER, DAS NICHTS ABSCHNEIDET.
       ======================================================================
       ORIGINALTEXT MARVIN (03.09.2026)
         „mach das popup fenster größer. diese abgeshnittene größen will ich
          nicht haben. das soll alles gut sichtbar in der gleichen größe
          sein!"

       Der Kalender und die Zeiten standen in 720 Pixeln nebeneinander: das
       Monatsgitter wurde schmal, die Zeiten fielen auf zwei enge Spalten, und
       der Rest rollte im Fenster. **Ein Fenster, in dem man rollen muss, um
       den Knopf zu sehen, den man drücken will, ist zu klein.** */
    <Dialog titel={bestehend ? 'Termin umbuchen' : 'Termin vergeben'}
            untertitel={baustelle(k)}
            /* DAS FENSTER NIMMT DIE BREITE, DIE DANEBEN LEER STEHT.
               ORIGINALTEXT MARVIN (06.09.2026)
                 „du solltest doch das popup größer machen, damit die tage
                  alle gleich groß sind und auch nichts nach unten gescrollt
                  werden muss. die ganzen anzeigen passen nicht auf meinem
                  15\" laptop."
               Nachgemessen bei 1366x768: das Fenster war 1040 breit, der Leib
               brauchte 1438 Bildpunkte Höhe und hatte 658 - also fehlten 780,
               und daneben lagen 326 Bildpunkte BREITE ungenutzt. Ein Fenster,
               das schmal bleibt und dafür rollt, verschenkt genau den Platz,
               der die Rollerei erspart. Gedeckelt wird es ohnehin gegen 96vw. */
            laeuft={laeuft} zu={zu} breite={1360}>
      <Fehlerkasten lage={fehler} />

      {/* WAS SCHON STEHT, STEHT GANZ OBEN.
          Nicht unten unter „Schon vergeben": wer verschiebt, muss beim ersten
          Blick sehen, WOVON er verschiebt. */}
      {bestehend && (
        <div className="hinweis warnung">
          <b>{bestehende.length > 1
            ? 'Diese Gewerke haben schon einen Termin:'
            : 'Dieses Gewerk hat schon einen Termin:'}</b>
          {/* JEDE ART EINZELN. Bei „Einblasen + Spleißen" können zwei
              Termine dastehen, und beide werden ersetzt - eine Zeile für
              beide verschwiege den zweiten. */}
          {bestehende.map((g: any) => (
            <div key={g.id}>
              {GEWERK_TEXT[g.art]}: {datum(g.termin_datum)}
              {g.termin_von ? `, ${uhr(g.termin_von)}` : ''}
              {g.termin_bis ? ` bis ${uhr(g.termin_bis)}` : ''}
              {g.firma ? `, ${g.firma}` : ''}
              {Number(g.bautage) > 1 ? `, ${g.bautage} Bautage` : ''}
            </div>
          ))}
          <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
            {firmaWechsel
              ? 'Gewählt ist eine ANDERE Firma - der Termin wechselt den '
                + 'Auftragnehmer.'
              : 'Was hier gewählt wird, ERSETZT ihn. Zum Absagen gibt es '
                + '„stornieren" unten in der Liste.'}
          </div>
        </div>
      )}
      {/* WER, WO UND WELCHES GEWERK - und dann der Kalender.
          ====================================================================
          Die Reihenfolge ist die des Bestands und nicht beliebig: das Gewerk
          entscheidet über die Regelzeiten und darüber, ob mehrere Tage
          überhaupt gehen; die FIRMA entscheidet, welche Belegung der Kalender
          zeigt; der Tiefbaubereich entscheidet, ob der Gemeindevorlauf gilt.
          Wer den Kalender vor der Firma zeigt, zeigt einen leeren Monat. */}
      {/* DIE STEUERUNG STEHT NEBEN DEM KALENDER, NICHT ÜBER UND UNTER IHM.
          ====================================================================
          ORIGINALTEXT MARVIN (06.09.2026)
            „du solltest doch das popup größer machen, damit die tage alle
             gleich groß sind und auch nichts nach unten gescrollt werden
             muss. die ganzen anzeigen passen nicht auf meinem 15\" laptop."

          NACHGEMESSEN, NICHT GESCHÄTZT: bei 1366x768 brauchte der Leib 1438
          Bildpunkte Höhe und hatte 658. Gestapelt kommt man da nie hin - der
          Kalender allein ist rund 550 hoch, und Gewerkwahl, Firma, Bereich,
          Hinweise, Knöpfe und „Schon vergeben" legen noch einmal so viel
          darauf. **Was in einer Spalte Platz hat, gehört nicht in eine Zeile
          darunter**: die Höhe des Fensters ist ab jetzt die des HÖHEREN von
          beiden und nicht die Summe.

          DIE REIHENFOLGE IM PROGRAMMTEXT BLEIBT DIE DES BEDIENERS - Gewerk,
          Firma, Bereich, Kalender, Hinweise, Knopf. Auf einem schmalen
          Bildschirm steht sie genau so untereinander; die zwei Spalten macht
          allein das Stylesheet, und zwar erst, wenn die Breite dafür da ist.
          Ein Umbau, der die Reihenfolge im Programmtext dreht, nähme dem
          Telefon die Reihenfolge weg. */}
      <div className="terminfenster">

      <div className="terminspalte">

      {/* VIER PUNKTE UND NICHT DREI PLUS EIN KÄSTCHEN.
          ====================================================================
          ORIGINALTEXT MARVIN (07.09.2026)
            „der vermerk das spleißen am gleichen tag stattfinden soll, ist zu
             klein, du machst oben noch einen gemeinsem punkt für Einblasen +
             SPleißen hin"

          „Einblasen + Spleißen" WAR schon buchbar - als Häkchen weiter unten
          in der rechten Spalte, mit einem Satz in Kleinschrift darunter. Es
          ist aber keine Nebenbedingung eines Einblastermins, sondern eine
          EIGENE Wahl: eine Fahrt statt zweier. **Eine Entscheidung, die man
          erst nach der Firma und nach dem Kalender trifft, trifft man beim
          zwanzigsten Mal nicht mehr** - dort steht sie nicht mehr zur Wahl,
          dort steht sie im Weg.

          DAS HÄKCHEN IST DAMIT WEG UND NICHT ZUSÄTZLICH. Zwei Wege zu
          derselben Sache sind kein doppelter Komfort, sondern eine halbe
          Auskunft: wer den einen kennt, erfährt vom anderen nie - und hier
          käme dazu, dass sie sich widersprechen können (oben „Einblasen",
          unten der Haken gesetzt).

          DER SERVERWEG BLEIBT UNANGETASTET. `auch_partner` gibt es seit
          langem, und `arten` dort baut daraus [EB, SP]. Was sich ändert, ist
          allein, WO diese Frage gestellt wird.

          UND DAS GEWERK BLEIBT EINES VON DREI. Der vierte Knopf wählt
          „Einblasen" UND setzt die Kopplung; der Server kennt kein Gewerk
          „EBSP", und eines zu erfinden hiesse, eine vierte Art in
          `gewerk_art` zu haben, die nie gebaut wird. */}
      <div className="reihe gewerkwahl">
        {[['TB', GEWERK_TEXT.TB], ['EB', GEWERK_TEXT.EB], ['SP', GEWERK_TEXT.SP],
          ['EBSP', 'Einblasen + Spleißen']].map(([w, text]) => {
          const gewaehlt = w === 'EBSP' ? kombi : (gewerk === w && !kombi);
          return (
            <button key={w} type="button" aria-pressed={gewaehlt}
                    className={'knopf' + (gewaehlt ? ' haupt' : '')}
                    onClick={() => {
                      if (w === 'EBSP') { setGewerk('EB'); setAuchPartner(true); }
                      else { setGewerk(w); setAuchPartner(false); }
                    }}>
              {text}
            </button>
          );
        })}
        <span className="klein leise">
          Die Doku bekommt keinen Termin auf der Baustelle.
        </span>
      </div>

      {/* DER VERMERK STEHT IN LESEGRÖSSE UND NICHT IN GRAU.
          ====================================================================
          Er stand als `hinweis` UNTER einem Kästchen, in 12px und leise - also
          in der kleinsten Schrift des Fensters, für die Auskunft, wegen der es
          den vierten Knopf überhaupt gibt. Jetzt ist es ein Kasten in
          Lesegröße, direkt unter der Wahl, auf die er sich bezieht.

          UND ER SAGT JE NACH WAHL ETWAS ANDERES. Ein Satz, der bei jeder
          Buchung derselbe ist, wird nach zwei Wochen nicht mehr gelesen: bei
          getrennter Wahl ist er eine WARNUNG mit dem Ausweg daneben, bei der
          gemeinsamen eine ZUSAGE darüber, was gleich gebucht wird. Beim
          Tiefbau steht er gar nicht - dort gibt es keinen Partner. */}
      {(gewerk === 'EB' || gewerk === 'SP') && (
        <div className={'hinweis' + (kombi ? '' : ' warnung')}>
          {kombi ? (
            <>
              <b>Einblasen und Spleißen zusammen.</b>
              Beide Gewerke werden mit demselben Tag, derselben Zeit und
              derselben Firma gebucht. Es fährt einmal jemand hin.
            </>
          ) : (
            <>
              <b>Spleißen findet am selben Tag statt.</b>
              Einblasen und Spleißen gehören zusammen; getrennt gebucht fährt
              zweimal jemand hin. Oben „Einblasen + Spleißen" bucht beides in
              einem Zug.
            </>
          )}
        </div>
      )}

      <div className="feldreihe">
        <Auswahl name="subunternehmer_id" titel="Firma" wert={sub} setze={setSub}
                 breite={340} fehler={fehler} leerText="noch offen"
                 /* NUR DIE FIRMA, NICHT DAS KURZZEICHEN.
                  * Marvin, 25.08.2026: „das kurzzeichen muss nicht in jedem
                  * termin stehen."
                  * Es ist der Schluessel, ueber den die
                  * Verfuegbarkeitsmeldungen aus dem Bestand verknuepft sind -
                  * ein Datenbankfeld also, kein Name. */
                 optionen={passende.map((s: any) => ({
                   wert: s.id, text: s.firma }))}
                 hinweis={passende.length < subs.filter((s: any) => s.aktiv).length
                   ? (kombi ? 'Nur Firmen, die Einblasen UND Spleißen führen.'
                            : 'Nur Firmen, die dieses Gewerk führen.')
                   : 'Der Kalender zeigt, wo diese Firma schon fährt.'} />
      </div>

      {/* ÖFFENTLICH ODER PRIVAT SIND DREI KNÖPFE UND KEINE LISTE.
          Der Bestand hatte sie als Knöpfe, und das aus einem Grund: an dieser
          Antwort hängt der Gemeindevorlauf, also die Frage, welche Tage im
          Kalender überhaupt vernünftig sind. Eine zugeklappte Liste beantwortet
          sie erst, wenn jemand sie aufklappt - und bis dahin rechnet der
          Kalender vorsichtshalber mit dem Vorlauf. */}
      {gewerk === 'TB' && (
        <div className="reihe bereichreihe">
          <span className="klein leise">Tiefbaubereich:</span>
          {(listen?.tb_bereich ?? KEINE).map((o: any) => (
            <button key={o.wert} type="button" aria-pressed={bereich === o.wert}
                    className={'knopf klein' + (bereich === o.wert ? ' haupt' : '')}
                    onClick={() => setBereich(o.wert)}>
              {o.text}
            </button>
          ))}
          {!bereich && (
            <span className="klein warnfarbe">
              Erst wählen: davon hängen Vorlauf, AAZ und VAO ab.
            </span>
          )}
        </div>
      )}

      </div>

      {/* DER KALENDER UND WAS SCHON STEHT - EIN KASTEN.
          Beides gehoert in DIESELBE Spalte und nicht in zwei Zeilen: eine
          Tabelle unter zwei Spalten legt sich auf die HOEHERE von beiden
          obendrauf. Nachgemessen: getrennt 742 Bildpunkte, zusammen 632. */}
      <div className="terminkalenderteil">

      {/* DER KALENDER. Er ist die Terminsteuerung und nicht ihre Zier: hier
          entscheidet sich, welcher Tag, welche Uhrzeit und wie viele Tage. */}
      <Terminkalender p={p} monat={monat} setMonat={setMonat} firma={sub}
                      tage={tage} setTage={tageSetzen} vorbelegt={vorbelegt}
                      mehrtag={!!p?.mehrtagErlaubt} />

      {/* „SCHON VERGEBEN" STEHT UNTER DEM KALENDER.
          In der 400 Bildpunkte schmalen Steuerspalte brach die Tabelle über
          vier Zeilen um und war 199 Bildpunkte hoch; hier sind es 125. Und
          sie gehört auch inhaltlich hierher: sie beantwortet nicht „was buche
          ich", sondern „was steht sonst noch". */}
      {/* ERLEDIGTE GEWERKE STEHEN HIER MIT, und bis zum 09.09.2026 nicht.
          ORIGINALTEXT MARVIN: „ich muss alte abgeschlossene aufträge wieder
          öffnen und neu terminieren können." Der Filter `!g.erledigt` nahm
          sie aus dieser Liste, und damit gab es fuer sie auch keine
          Zeilenknoepfe mehr - **ein Auftrag, den man nicht sieht, laesst sich
          nicht umbuchen.** Sie stehen jetzt da, als erledigt gekennzeichnet:
          wer noch einmal hinfaehrt, findet den alten Termin und bucht
          daneben. */}
      {d.gewerke?.some((g: any) => g.termin_datum) && (
        <div className="schonvergeben">
          {/* DIE ÜBRIGEN GEWERKE. Das gerade bearbeitete steht oben im
              Kasten - zweimal dasselbe zu lesen ist einmal zu viel. */}
          <h4 style={{ margin: '0 0 6px' }}>Schon vergeben</h4>
          <table className="tabelle">
            <tbody>
              {d.gewerke.filter((g: any) => g.termin_datum)
                .map((g: any) => (
                <tr key={g.id}>
                  <td>{GEWERK_TEXT[g.art]}</td>
                  <td>{datum(g.termin_datum)} {uhr(g.termin_von)}</td>
                  <td>{g.firma ?? <span className="leise">offen</span>}</td>
                  <td style={{ width: 190 }}>
                    <TerminZeilenknoepfe g={g} auftragId={auftragId} fertig={fertig} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      </div>

      <div className="terminspalte">


      {/* HIER STAND DAS HÄKCHEN „Spleißen am selben Tag mitbuchen".
          Es ist zum vierten Knopf oben geworden (siehe dort). An dieser
          Stelle bleibt nichts stehen - ein Kästchen, das dasselbe noch einmal
          fragt, wäre der zweite Weg zu einer Sache. */}
      {/* DER TIEFBAUAUFWAND STEHT DANEBEN, wenn ein Tiefbautermin gebucht
          wird. Marvin, 03.09.2026: „die info zum tiefbauaufwand sollten wir
          noch als block in die auftragsakte bringen und hier bei dem
          terminsteuerungsfenster mit anzeigen." Hier entscheidet sich, wie
          viele Tage gebucht werden - die Zahl gehört an die Stelle, an der die
          Frage entsteht, und nicht zwei Fenster weiter. */}
      {/* UND OHNE SCHÄTZUNG STEHT DA, DASS ES KEINE GIBT - sonst sieht das
          Fenster genauso aus wie vorher, und „hier mit anzeigen" wäre
          unerfüllt geblieben, ohne dass es auffällt. */}
      {/* DIE HINWEISE UNTER DEM KALENDER STEHEN NEBENEINANDER, NICHT
          UNTEREINANDER.
          ORIGINALTEXT MARVIN (06.09.2026)
            „du solltest doch das popup größer machen, damit die tage alle
             gleich groß sind und auch nichts nach unten gescrollt werden
             muss. die ganzen anzeigen passen nicht auf meinem 15" laptop."
          NACHGEMESSEN AUF 1366x768: der Leib des Fensters trug 1438 Bildpunkte
          Inhalt in 658 Bildpunkten Sicht - es fehlten 780. Davon standen 363
          in genau diesen vier Kästen, jeder über die ganze Breite und jeder
          mit drei Wörtern darin. Zwei Spalten halbieren das, ohne dass ein
          Wort wegfällt: ein Hinweis ist ein Satz und keine Tabelle.
          Auf einem schmalen Bildschirm bleibt es bei einer Spalte - dort ist
          die Breite knapp und nicht die Höhe. */}
      <div className="terminfuss">

      {gewerk === 'TB' && d.bloecke?.planung && !d.tiefbauaufwand && (
        <div className="hinweis">
          <b>Geschätzter Aufwand:</b> noch keiner. Er entsteht unter
          „Vorbereiten", Reiter „Planung", aus dem eingezeichneten
          Leitungsweg.
        </div>
      )}

      {gewerk === 'TB' && d.tiefbauaufwand && (
        <div className="hinweis">
          <b>Geschätzter Aufwand:</b>{' '}
          {menge(d.tiefbauaufwand.meter)} m,{' '}
          {menge(d.tiefbauaufwand.stunden)} Stunden,{' '}
          {menge(d.tiefbauaufwand.tage)}{' '}
          {Number(d.tiefbauaufwand.tage) === 1 ? 'Tag' : 'Tage'}
          {d.tiefbauaufwand.montagegruben
            ? `, ${d.tiefbauaufwand.montagegruben} Montagegrube`
              + `${d.tiefbauaufwand.montagegruben === 1 ? '' : 'n'}` : ''}
          {Number(d.tiefbauaufwand.tage) > 1 && (
            <div className="klein">
              Über einem Tag: im Kalender den Folgetag dazuklicken oder unten
              „2 Tage" nehmen - sonst steht die Kolonne am zweiten Tag ohne
              Buchung da.
            </div>
          )}
        </div>
      )}

      {p && (
        <div className="terminfussteil">
          {!p.riegel.erlaubt && (
            <div className="hinweis fehler"><b>Hält auf: </b>{p.riegel.grund}</div>
          )}
          {p.riegel.erlaubt && p.riegel.hinweis && (
            <div className="hinweis warnung">{p.riegel.hinweis}</div>
          )}
          {/* HIER STAND DER GEMEINDEVORLAUF ALS STEHENDE ERINNERUNG.
              ORIGINALTEXT MARVIN (03.09.2026)
                „straffierung in die legende unten bringen, die erinnerung
                 unten kann dann weg"
              Die Auskunft ist nicht weg, sie steht jetzt DORT, WO SIE GILT:
              in der Legende unter dem Kalender, neben der Schraffur, die
              genau diese Tage trägt. Ein Kasten, der bei jedem Termin
              dasselbe sagt, wird nach zwei Wochen nicht mehr gelesen - und
              dann auch an dem Tag nicht, an dem er etwas anderes sagt.
              GEBLIEBEN IST, WAS WIRKLICH GREIFT: liegt der GEWÄHLTE Tag
              davor, färbt sich der Legendeneintrag und nennt es. Das ist
              kein Merkposten mehr, sondern ein Befund. */}
          {p.vorlauf?.gilt && p.vorlauf.ausStandard && p.zuFrueh?.zuFrueh && (
            <div className="hinweis warnung">
              Für {p.vorlauf.gemeinde ?? 'diese Gemeinde'} ist kein eigener
              Vorlauf hinterlegt; es gilt der Standard von
              {' '}{p.vorlauf.tage} Werktagen.
            </div>
          )}
          {p.anordnungNoetig && (
            <div className={'hinweis ' + (p.gedeckt ? '' : 'warnung')}>
              {p.gedeckt
                ? 'Der Tag ist von einer gültigen Anordnung gedeckt.'
                : 'Für diesen Tag liegt keine gültige verkehrsrechtliche '
                  + 'Anordnung vor. Der Termin lässt sich buchen, AAZ und VAO '
                  + 'gehören danach unter "Behörde" erzeugt.'}
            </div>
          )}
        </div>
      )}

      </div>

      {/* EIN GRAUER KNOPF OHNE GRUND SIEHT AUS WIE EIN KAPUTTER KNOPF.
          Er steht hier zweimal grau: ohne gewählten Tag und wenn ein Riegel
          hält. Der Riegel sagt seinen Grund oben; der fehlende Tag sagte gar
          nichts. */}
      {!tage.length && (
        <div className="klein leise fehlttag">
          Es fehlt der Tag: einen im Kalender anklicken.
        </div>
      )}

      <div className="reihe terminknoepfe">
        <button className="knopf haupt"
                disabled={laeuft || !tage.length || gesperrt}
                onClick={async () => {
                  setLaeuft(true); setFehler(null);
                  try {
                    const r = await api.terminBuchen(auftragId, {
                      gewerk,
                      /* DER ERSTE TAG IST DER TERMIN. Alles, was heute liest,
                         liest damit weiterhin richtig - und der Server prüft
                         genau das nach. */
                      datum: tage[0].iso, von: tage[0].von, bis: tage[0].bis,
                      subunternehmer_id: sub || null,
                      tb_bereich: gewerk === 'TB' ? (bereich || null) : null,
                      /* GESCHICKT WIRD, WAS OBEN GEWÄHLT IST. `kombi` ist
                         dieselbe Rechnung, die auch die Firmenliste und
                         „schon vergeben" benutzen - nicht der rohe Merker:
                         beim Tiefbau gibt es keinen Partner, und ein
                         stehengebliebenes `true` wäre dort eine Angabe, die
                         niemand gemacht hat. */
                      auch_partner: kombi,
                      /* GEBUCHT WIRD, WAS IM KALENDER STEHT.
                         Die Tage kommen aus der Leiste unter dem Kalender, mit
                         ihren eigenen Zeiten - ein halber letzter Tag ist bei
                         einem Tiefbau der Normalfall. Es gibt keine zweite
                         Rechnung dahinter: was die Chips zeigen, geht hinaus. */
                      tage: tage.length > 1 ? tage : undefined,
                    });
                    /* DIE ZWEI RÜCKFRAGEN, in der Reihenfolge des Bestands:
                       erst die Firma, dann der Kunde. Sie kommen NACH der
                       Buchung - ein Termin, der an einer abgelehnten Frage
                       hängt, wäre kein Termin. */
                    if (r?.nachfrage) { setNachfrage(r.nachfrage); return; }
                    await fertig();
                  } catch (f) { setFehler(ausFehler(f)); }
                  finally { setLaeuft(false); }
                }}>
          {laeuft ? 'Einen Moment'
                  : firmaWechsel ? 'Firma wechseln'
                  : bestehend ? 'Umbuchen' : 'Buchen'}
        </button>
        <button className="knopf" onClick={zu} disabled={laeuft}>Abbrechen</button>
      </div>

      </div>

      </div>
    </Dialog>
  );
}


function TerminZeilenknoepfe({ g, auftragId, fertig }: any) {
  const [was, setWas] = useState<null | 'storno' | 'erledigt' | 'oeffnen'>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  /* NACH DEM STORNO: DEN KUNDEN FRAGEN (.354). Marvin, 13.10.2026: „Ja baue
     es ein." */
  const [absage, setAbsage] = useState<any>(null);
  if (absage) {
    return (
      <Nachfragen auftragId={absage.auftragId ?? g.auftrag_id} nachfrage={absage}
                  fertig={async () => { setAbsage(null); await fertig(); }} />
    );
  }

  if (was === 'storno') {
    return (
      <TerminStornoFenster
        titel="Termin stornieren" auftragId={String(g.auftrag_id ?? auftragId)} arten={[String(g.art)]}
        erklaerung={'Der Termin fällt aus der Planung. Der Grund gehört in die Akte: bei der nächsten Vergabe will man wissen, warum.'
          + (g.art === 'EB' || g.art === 'SP'
            ? ' Liegen Einblasen und Spleißen auf demselben Termin, werden beide storniert. Ob die Firma eine Meldung bekommt, wirst du danach gefragt.' : '')}
        laeuft={laeuft} fehler={fehler} zu={() => setWas(null)}
        fertig={async (grund, gbgs) => {
          setLaeuft(true); setFehler(null);
          try {
            /* ERST FRAGEN, DANN PUSHEN (.358). */
            const push = await pushFrage(g.termin_datum);
            const r = await api.terminStorno(g.id, grund, [], push, gbgs);
            if (r?.nachfrage) { setWas(null); setAbsage(r.nachfrage); return; }
            await fertig();
          }
          catch (f) { setFehler(ausFehler(f)); }
          finally { setLaeuft(false); }
        }}
      />
    );
  }
  /* WIEDER ÖFFNEN, MIT GRUND. Der Weg dahinter gibt es seit Migration 001
     (`POST /termine/:id/erledigt` mit `erledigt: false`) und wurde von keiner
     Zeile gerufen: `api.terminErledigt(g.id, {})` unten schickt einen leeren
     Rumpf, und der heisst „erledigt". Der Beleg stand in den Daten - 0 Zeilen
     `wieder_offen` im Terminverlauf, obwohl der Wert seit jeher im CHECK
     steht. */
  if (was === 'oeffnen') {
    return (
      <MitGrund
        titel="Gewerk wieder öffnen" knopf="Wieder öffnen"
        erklaerung="Der Haken „erledigt“ fällt, der Termin bleibt stehen.
                    Danach lässt sich ein neuer buchen. Aufmaß, Dateien und
                    Kommentare bleiben unangetastet."
        laeuft={laeuft} fehler={fehler} zu={() => setWas(null)}
        fertig={async (grund) => {
          setLaeuft(true); setFehler(null);
          try {
            await api.terminErledigt(g.id, { erledigt: false, grund });
            await fertig();
          } catch (f) { setFehler(ausFehler(f)); }
          finally { setLaeuft(false); }
        }}
      />
    );
  }
  /* EIN ERLEDIGTES GEWERK HAT ANDERE KNÖPFE ALS EIN OFFENES. „erledigt" ein
     zweites Mal zu drücken tut nichts, und „stornieren" tut das Falsche: der
     Termin ist gefahren worden. */
  if (g.erledigt) {
    return (
      <div className="reihe">
        <span className="merker gruen">erledigt {datum(g.erledigt_am)}</span>
        <button className="knopf klein" onClick={() => setWas('oeffnen')}>
          wieder öffnen
        </button>
      </div>
    );
  }
  return (
    <div className="reihe">
      <button className="knopf klein" disabled={laeuft}
              onClick={async () => {
                setLaeuft(true);
                try { await api.terminErledigt(g.id, {}); await fertig(); }
                catch (f) { setFehler(ausFehler(f)); }
                finally { setLaeuft(false); }
              }}>
        erledigt
      </button>
      <button className="knopf klein" onClick={() => setWas('storno')}>
        stornieren
      </button>
    </div>
  );
}
